import React, { useState, useMemo, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  CheckSquare,
  Settings,
  Video,
  Calendar as CalendarIcon,
  ListTodo,
  RefreshCw,
  Car,
  AlignLeft,
  Pencil,
  Trash2,
  Check
} from 'lucide-react';
import { createTask, updateTask, createEvent, updateEvent, deleteEvent, getSettings, updateSettings } from '../api';
import {
  useEvents,
  useTasks,
  useInvalidateEvents,
  useInvalidateTasks,
  useSetTasksData,
  useSetEventsData,
  usePrefetchAdjacentEvents,
} from '../hooks/useCalendarData';
import EventModal from './EventModal';
import EventViewModal from './EventViewModal';
import TaskModal from './TaskModal';
import SettingsModal from './SettingsModal';
import { Badge } from './catalyst/badge';
import { Button } from './catalyst/button';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from './catalyst/dialog';
import { Input } from './catalyst/input';
import { Switch } from './catalyst/switch';
import { Textarea } from './catalyst/textarea';

// --- CONSTANTS ---
const VIEW_TYPES = { DAY: 'Day', WEEK: 'Week', SEVEN_DAYS: '7 Days', MONTH: 'Month' };
const DAYS_SHORT = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const PENDING_DELETE_HIDE_MS = 60 * 1000;
const SIDEBAR_WIDTH_STORAGE_KEY = 'calendarSidebarWidth';
const DEFAULT_SIDEBAR_WIDTH = 414;
const MIN_SIDEBAR_WIDTH = 280;
const MAX_SIDEBAR_WIDTH = 760;
const MIN_MAIN_WIDTH = 560;
const MOBILE_FAB_BOTTOM_OFFSET = 'calc(75px + max(12px, env(safe-area-inset-bottom)))';
const TOUCH_TAP_MOVE_THRESHOLD = 10;
const MOBILE_DAY_SWIPE_MIN_X = 56;
const MOBILE_DAY_SWIPE_MAX_Y = 72;
const MOBILE_DAY_SWIPE_DOMINANCE = 1.35;
const TIME_GRID_SNAP_MINUTES = 15;
const MIN_EVENT_DURATION_MINUTES = 15;
const MIN_EVENT_DURATION_MS = MIN_EVENT_DURATION_MINUTES * 60 * 1000;
const TASK_EVENT_LINK_KEYS = {
  taskId: 'calendarAppTaskId',
  taskListId: 'calendarAppTaskListId',
};
const EVENT_TONES = {
  '1': {
    badge: 'blue',
    event: 'border-blue-700/20 bg-blue-600 text-white shadow-blue-950/10 hover:bg-blue-500 active:bg-blue-700',
    softCard: 'border-blue-200 bg-blue-50 text-blue-700 ring-blue-600/10',
    ghost: 'border-blue-500 bg-blue-50/90 text-blue-700',
    travel: 'border-blue-200 bg-blue-50/90 text-blue-700',
    dot: 'bg-blue-500'
  },
  '2': {
    badge: 'rose',
    event: 'border-rose-700/20 bg-rose-600 text-white shadow-rose-950/10 hover:bg-rose-500 active:bg-rose-700',
    softCard: 'border-rose-200 bg-rose-50 text-rose-700 ring-rose-600/10',
    ghost: 'border-rose-500 bg-rose-50/90 text-rose-700',
    travel: 'border-rose-200 bg-rose-50/90 text-rose-700',
    dot: 'bg-rose-500'
  },
  '3': {
    badge: 'emerald',
    event: 'border-emerald-700/20 bg-emerald-600 text-white shadow-emerald-950/10 hover:bg-emerald-500 active:bg-emerald-700',
    softCard: 'border-emerald-200 bg-emerald-50 text-emerald-700 ring-emerald-600/10',
    ghost: 'border-emerald-500 bg-emerald-50/90 text-emerald-700',
    travel: 'border-emerald-200 bg-emerald-50/90 text-emerald-700',
    dot: 'bg-emerald-500'
  },
  '4': {
    badge: 'zinc',
    event: 'border-zinc-700/20 bg-zinc-600 text-white shadow-zinc-950/10 hover:bg-zinc-500 active:bg-zinc-700',
    softCard: 'border-zinc-200 bg-zinc-100 text-zinc-700 ring-zinc-600/10',
    ghost: 'border-zinc-500 bg-zinc-100/90 text-zinc-700',
    travel: 'border-zinc-200 bg-zinc-100/90 text-zinc-700',
    dot: 'bg-zinc-500'
  },
  dimmed: {
    badge: 'zinc',
    event: 'border-zinc-300 bg-zinc-400 text-white shadow-zinc-950/5 hover:bg-zinc-500 active:bg-zinc-600',
    softCard: 'border-zinc-200 bg-zinc-100 text-zinc-500 ring-zinc-600/10',
    ghost: 'border-zinc-400 bg-zinc-100/90 text-zinc-600',
    travel: 'border-zinc-200 bg-zinc-100/90 text-zinc-600',
    dot: 'bg-zinc-400'
  }
};

const getTravelTime = (event) => ({
  before: parseInt(event.extendedProperties?.shared?.travelBefore || '0', 10),
  after: parseInt(event.extendedProperties?.shared?.travelAfter || '0', 10)
});

const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const getDateKey = (date) => date.toDateString();

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const isBlockedCalendar = (event) => {
  return event.calendarName?.toLowerCase() === 'blocked';
};

const VIDEO_PATTERNS = /meet\.google\.com|zoom\.us|teams\.microsoft\.com|webex\.com|whereby\.com/i;
const hasVideoConference = (event) => {
  if (event?.hangoutLink || event?.conferenceData?.entryPoints?.[0]?.uri) return true;
  if (event?.location && VIDEO_PATTERNS.test(event.location)) return true;
  if (event?.description && VIDEO_PATTERNS.test(event.description)) return true;
  return false;
};

// Video events override to green (color '3')
const getEventColors = (event, dimmed) => {
  if (dimmed) return { tone: EVENT_TONES.dimmed };
  if (hasVideoConference(event)) return { tone: EVENT_TONES['3'] };
  const colorId = event.colorId || '1';
  return { tone: EVENT_TONES[colorId] || EVENT_TONES['1'] };
};

const formatEventTimeRange = (event) => {
  if (!event.start?.dateTime) return 'all-day';
  const start = new Date(event.start.dateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  const end = event.end?.dateTime
    ? new Date(event.end.dateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    : '';
  return end ? `${start} - ${end}` : start;
};

const formatTravelDuration = (minutes) => {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
  }
  return `${minutes} min`;
};

const getEventCalendarId = (event) => event?.calendarId || event?.organizer?.email || 'primary';

const buildPendingDeleteMarker = (event, deleteAll = false) => {
  const seriesId = deleteAll ? (event.recurringEventId || event.id) : null;

  return {
    key: `${getEventCalendarId(event)}:${event.id}:${seriesId || ''}`,
    calendarId: getEventCalendarId(event),
    eventId: event.id,
    seriesId,
  };
};

const eventMatchesPendingDelete = (event, marker) => {
  if (!event || !marker) return false;
  if (getEventCalendarId(event) !== marker.calendarId) return false;
  if (event.id === marker.eventId) return true;

  return Boolean(marker.seriesId && (
    event.id === marker.seriesId ||
    event.recurringEventId === marker.seriesId
  ));
};

const getEventPrivateProperties = (event) => event?.extendedProperties?.private || {};

const getLinkedTaskId = (event) => getEventPrivateProperties(event)[TASK_EVENT_LINK_KEYS.taskId] || '';

const buildTaskEventExtendedProperties = (task) => ({
  private: {
    [TASK_EVENT_LINK_KEYS.taskId]: String(task.id),
    [TASK_EVENT_LINK_KEYS.taskListId]: String(task.taskListId || ''),
  },
});

const clampSidebarWidth = (width) => {
  const viewportMax = typeof window === 'undefined'
    ? MAX_SIDEBAR_WIDTH
    : Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, window.innerWidth - MIN_MAIN_WIDTH));

  return Math.round(Math.max(MIN_SIDEBAR_WIDTH, Math.min(width, viewportMax)));
};

const isLinkedTaskChecked = (task, completing) => task?.status === 'completed' || completing;

function LinkedTaskCheckmark({ checked, compact = false, onToggle, className = '' }) {
  const label = checked ? 'Mark linked task incomplete' : 'Mark linked task complete';

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      draggable={false}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle?.(e);
      }}
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-full border transition focus:outline-none focus:ring-2 focus:ring-white/80',
        compact ? 'h-3.5 w-3.5 border' : 'h-4 w-4 border-2',
        checked
          ? 'border-zinc-950 bg-zinc-950 text-white shadow-sm'
          : 'border-current/40 bg-white/80 text-transparent hover:bg-white hover:text-zinc-400',
        className
      )}
    >
      <Check className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
    </button>
  );
}

function handleKeyboardClick(e, onClick) {
  if (!onClick || (e.key !== 'Enter' && e.key !== ' ')) return;
  e.preventDefault();
  onClick(e);
}

function CalendarEventBadge({
  event,
  dimmed = false,
  past = false,
  compact = false,
  linkedTask,
  linkedTaskCompleting = false,
  onToggleLinkedTask,
  onClick,
  onContextMenu
}) {
  const { tone } = getEventColors(event, dimmed || past);
  const linkedTaskChecked = isLinkedTaskChecked(linkedTask, linkedTaskCompleting);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => handleKeyboardClick(e, onClick)}
      onContextMenu={onContextMenu}
      className={clsx(
        'group mb-1 block w-full cursor-pointer rounded-md text-left outline-none transition',
        'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
        (dimmed || past || isBlockedCalendar(event)) && 'opacity-60'
      )}
    >
      <Badge
        color={tone.badge}
        className={clsx(
          'w-full min-w-0 justify-start truncate shadow-sm transition group-hover:ring-zinc-900/15',
          compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'
        )}
      >
        {linkedTask && (
          <LinkedTaskCheckmark
            checked={linkedTaskChecked}
            compact={compact}
            onToggle={onToggleLinkedTask}
          />
        )}
        {hasVideoConference(event) && <Video className={clsx('shrink-0', compact ? 'h-2.5 w-2.5' : 'h-3 w-3')} />}
        <span className={clsx('min-w-0 flex-1 truncate', linkedTaskChecked && 'line-through opacity-70')}>
          {event.summary || '(No title)'}
        </span>
      </Badge>
    </div>
  );
}

function CalendarListEventCard({
  event,
  dimmed = false,
  past = false,
  linkedTask,
  linkedTaskCompleting = false,
  onToggleLinkedTask,
  onClick,
  onContextMenu
}) {
  const { tone } = getEventColors(event, dimmed || past);
  const linkedTaskChecked = isLinkedTaskChecked(linkedTask, linkedTaskCompleting);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => handleKeyboardClick(e, onClick)}
      onContextMenu={onContextMenu}
      className={clsx(
        'relative flex w-full cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 text-left shadow-sm ring-1 ring-inset transition',
        'active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        tone.softCard,
        (dimmed || past || isBlockedCalendar(event)) && 'opacity-60'
      )}
    >
      {linkedTask ? (
        <LinkedTaskCheckmark
          checked={linkedTaskChecked}
          onToggle={onToggleLinkedTask}
          className="mt-0.5"
        />
      ) : (
        <span className={clsx('mt-1 h-2 w-2 shrink-0 rounded-full', tone.dot)} />
      )}
      <span className="min-w-0 flex-1">
        <span className={clsx('block truncate text-sm font-semibold', linkedTaskChecked && 'line-through opacity-70')}>
          {event.summary || '(No title)'}
        </span>
        <span className="mt-1 flex items-center gap-2 text-xs opacity-75">
          <span>{formatEventTimeRange(event)}</span>
          {hasVideoConference(event) && <Video className="h-3.5 w-3.5" />}
        </span>
      </span>
    </div>
  );
}

function MobileFloatingActionButton({ label, onClick, children }) {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-50 flex justify-end"
      style={{
        paddingBottom: MOBILE_FAB_BOTTOM_OFFSET,
        paddingLeft: 'max(16px, env(safe-area-inset-left))',
        paddingRight: 'max(16px, env(safe-area-inset-right))',
      }}
    >
      <Button
        aria-label={label}
        title={label}
        onClick={onClick}
        className="pointer-events-auto h-14 w-14 shrink-0 rounded-2xl p-0 active:scale-95"
        style={{
          border: '2px solid white',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
      >
        {children}
      </Button>
    </div>
  );
}

export default function Calendar({
  user,
  onLogout,
  googleConnected = false,
  googleUser,
  onConnectGoogle,
  onDisconnectGoogle,
  mobileTab: externalMobileTab,
  onMobileTabChange,
  renderBottomBar
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(VIEW_TYPES.SEVEN_DAYS);
  const [miniCalDate, setMiniCalDate] = useState(new Date());
  const [now, setNow] = useState(new Date());

  const showDisconnectedCalendar = !googleConnected && (
    process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
  const canRenderCalendar = googleConnected || showDisconnectedCalendar;
  const eventLookbackDays = window.innerWidth < 768 ? 7 : 0;
  const { data: events = [], isLoading: loading } = useEvents(currentDate, view, eventLookbackDays, googleConnected);
  usePrefetchAdjacentEvents(currentDate, view, eventLookbackDays, googleConnected);
  const { data: tasks = [] } = useTasks(googleConnected);
  const invalidateEvents = useInvalidateEvents();
  const invalidateTasks = useInvalidateTasks();
  const setTasksData = useSetTasksData();
  const setEventsData = useSetEventsData();
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventView, setShowEventView] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventContextMenu, setEventContextMenu] = useState(null);
  const eventContextMenuRef = React.useRef(null);
  const eventContextMenuOpenedAtRef = React.useRef(0);
  const suppressNextEventClickRef = React.useRef(false);
  const suppressNextEventClickTimeoutRef = React.useRef(null);
  const pendingDeleteTimersRef = React.useRef(new Map());
  const pendingDeleteKeysRef = React.useRef(new Set());
  const [pendingDeletedEvents, setPendingDeletedEvents] = useState([]);
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskNotes, setNewTaskNotes] = useState('');
  const [addTaskExpanded, setAddTaskExpanded] = useState(false);
  const taskInputRef = React.useRef(null);
  const [showTodoTasks, setShowTodoTasks] = useState(true);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const draggedTaskRef = React.useRef(null);
  const taskDropHoverSlotRef = React.useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [tappedSlot, setTappedSlot] = useState(null);
  const dragCreateRef = React.useRef(null);
  const dragOverlayRef = React.useRef(null);
  const dragMoveRef = React.useRef(null);
  const dragMoveOverlayRef = React.useRef(null);
  const eventResizeRef = React.useRef(null);
  const activeDaysRef = React.useRef([]);
  const columnRefs = React.useRef({});
  const hourlyGridScrollPositionsRef = React.useRef(new Map());
  const hourlyGridScrollContextRef = React.useRef('');
  const mobileDaySwipeRef = React.useRef({ tracking: false, pointerId: null, startX: 0, startY: 0 });
  const [eventResizePreview, setEventResizePreview] = useState(null);
  const [completingTasks, setCompletingTasks] = useState(new Set());
  const [hiddenCalendars, setHiddenCalendars] = useState(() => {
    const saved = localStorage.getItem('hiddenCalendars');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [dimKeywords, setDimKeywords] = useState(() => {
    const saved = localStorage.getItem('dimKeywords');
    return saved ? JSON.parse(saved) : [];
  });
  const [secondaryTimezone, setSecondaryTimezone] = useState(() => {
    return localStorage.getItem('secondaryTimezone') || '';
  });
  const [quickCreateEnabled, setQuickCreateEnabled] = useState(() => {
    const saved = localStorage.getItem('quickCreateEnabled');
    return saved !== null ? saved === 'true' : true; // on by default
  });
  const [quickCreateData, setQuickCreateData] = useState(null); // { startDate, endDate, top, left, width }
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = Number(localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY));
    return clampSidebarWidth(Number.isFinite(saved) && saved > 0 ? saved : DEFAULT_SIDEBAR_WIDTH);
  });
  const sidebarResizeRef = React.useRef({ startX: 0, startWidth: DEFAULT_SIDEBAR_WIDTH });
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);

  const clearPendingDeleteMarker = useCallback((markerKey) => {
    const timeoutId = pendingDeleteTimersRef.current.get(markerKey);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      pendingDeleteTimersRef.current.delete(markerKey);
    }

    pendingDeleteKeysRef.current.delete(markerKey);
    setPendingDeletedEvents((prev) => prev.filter(marker => marker.key !== markerKey));
  }, []);

  const holdPendingDeleteMarker = useCallback((marker) => {
    pendingDeleteKeysRef.current.add(marker.key);
    setPendingDeletedEvents((prev) => [
      ...prev.filter(existing => existing.key !== marker.key),
      marker,
    ]);

    const existingTimeoutId = pendingDeleteTimersRef.current.get(marker.key);
    if (existingTimeoutId) window.clearTimeout(existingTimeoutId);

    const timeoutId = window.setTimeout(() => {
      pendingDeleteTimersRef.current.delete(marker.key);
      pendingDeleteKeysRef.current.delete(marker.key);
      setPendingDeletedEvents((prev) => prev.filter(existing => existing.key !== marker.key));
    }, PENDING_DELETE_HIDE_MS);

    pendingDeleteTimersRef.current.set(marker.key, timeoutId);
  }, []);

  const persistSidebarWidth = useCallback((width) => {
    const nextWidth = clampSidebarWidth(width);
    setSidebarWidth(nextWidth);
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(nextWidth));
    return nextWidth;
  }, []);

  const handleSidebarResizeStart = useCallback((e) => {
    if (e.button !== 0) return;

    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    sidebarResizeRef.current = {
      startX: e.clientX,
      startWidth: sidebarWidth,
    };
    setIsResizingSidebar(true);
  }, [sidebarWidth]);

  const handleSidebarResizeKeyDown = useCallback((e) => {
    const step = e.shiftKey ? 40 : 16;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      persistSidebarWidth(sidebarWidth - step);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      persistSidebarWidth(sidebarWidth + step);
    } else if (e.key === 'Home') {
      e.preventDefault();
      persistSidebarWidth(MIN_SIDEBAR_WIDTH);
    } else if (e.key === 'End') {
      e.preventDefault();
      persistSidebarWidth(MAX_SIDEBAR_WIDTH);
    }
  }, [persistSidebarWidth, sidebarWidth]);

  const resetSidebarWidth = useCallback(() => {
    persistSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
  }, [persistSidebarWidth]);

  useEffect(() => {
    if (!isResizingSidebar) return undefined;

    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    const handlePointerMove = (e) => {
      const delta = e.clientX - sidebarResizeRef.current.startX;
      persistSidebarWidth(sidebarResizeRef.current.startWidth + delta);
    };

    const handlePointerUp = () => {
      setIsResizingSidebar(false);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isResizingSidebar, persistSidebarWidth]);

  // Sync settings from Supabase on mount
  useEffect(() => {
    if (!googleConnected) return;

    getSettings().then(settings => {
      if (settings.dimKeywords?.length) {
        setDimKeywords(settings.dimKeywords);
        localStorage.setItem('dimKeywords', JSON.stringify(settings.dimKeywords));
      }
      if (settings.hiddenCalendars?.length) {
        setHiddenCalendars(new Set(settings.hiddenCalendars));
        localStorage.setItem('hiddenCalendars', JSON.stringify(settings.hiddenCalendars));
      }
      if (settings.secondaryTimezone) {
        setSecondaryTimezone(settings.secondaryTimezone);
        localStorage.setItem('secondaryTimezone', settings.secondaryTimezone);
      }
      if (settings.quickCreateEnabled !== undefined) {
        setQuickCreateEnabled(settings.quickCreateEnabled);
        localStorage.setItem('quickCreateEnabled', String(settings.quickCreateEnabled));
      }
    }).catch(() => {});
  }, [googleConnected]);

  const handleQuickCreateEnabledChange = (enabled) => {
    setQuickCreateEnabled(enabled);
    localStorage.setItem('quickCreateEnabled', String(enabled));
    if (googleConnected) updateSettings({ quickCreateEnabled: enabled }).catch(() => {});
  };

  const handleDimKeywordsChange = (keywords) => {
    setDimKeywords(keywords);
    localStorage.setItem('dimKeywords', JSON.stringify(keywords));
    if (googleConnected) updateSettings({ dimKeywords: keywords }).catch(() => {});
  };

  const handleSecondaryTimezoneChange = (tz) => {
    setSecondaryTimezone(tz);
    localStorage.setItem('secondaryTimezone', tz);
    if (googleConnected) updateSettings({ secondaryTimezone: tz }).catch(() => {});
  };

  // Get short timezone label (e.g. "EST", "GMT", "JST")
  const getTimezoneAbbr = (tz) => {
    if (!tz) return '';
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
      const tzPart = parts.find(p => p.type === 'timeZoneName');
      return tzPart?.value || tz.split('/').pop().replace(/_/g, ' ');
    } catch { return tz.split('/').pop().replace(/_/g, ' '); }
  };

  // Convert a local hour (0-23) to the hour in a target timezone for display
  const getHourInTimezone = (localHour, tz) => {
    if (!tz) return null;
    try {
      const d = new Date();
      d.setHours(localHour, 0, 0, 0);
      const formatter = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false });
      return formatter.format(d);
    } catch { return null; }
  };

  const localTimezoneAbbr = getTimezoneAbbr(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const secondaryTimezoneAbbr = getTimezoneAbbr(secondaryTimezone);
  const timeColumnWidth = secondaryTimezone ? 78 : 50;

  const isEventDimmed = (event) => {
    if (!dimKeywords.length || !event.summary) return false;
    const title = event.summary.toLowerCase();
    return dimKeywords.some(kw => title.includes(kw.toLowerCase()));
  };

  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [internalMobileTab, setInternalMobileTab] = useState('calendar');
  const mobileTab = externalMobileTab || internalMobileTab;
  const setMobileTab = onMobileTabChange || setInternalMobileTab;
  const [mobileShowCompleted, setMobileShowCompleted] = useState(false); // collapsed by default
  const [mobileCalendarView, setMobileCalendarView] = useState('month'); // 'month' | '3day' | '1day'
  const mobileMiniCalendarExpanded = mobileCalendarView === 'month';
  const isMobileHourlyView = isMobile && (mobileCalendarView === '3day' || mobileCalendarView === '1day');
  const hourlyGridScrollContext = `${view}:${getDateKey(currentDate)}:${isMobile ? mobileCalendarView : 'desktop'}`;

  if (hourlyGridScrollContextRef.current !== hourlyGridScrollContext) {
    hourlyGridScrollPositionsRef.current.clear();
    hourlyGridScrollContextRef.current = hourlyGridScrollContext;
  }

  // Detect mobile screen
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setSidebarWidth((width) => clampSidebarWidth(width));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter and group once per data/settings change instead of on every render branch.
  const visibleEvents = useMemo(
    () => events.filter(event =>
      !hiddenCalendars.has(event.calendarId) &&
      !pendingDeletedEvents.some(marker => eventMatchesPendingDelete(event, marker))
    ),
    [events, hiddenCalendars, pendingDeletedEvents]
  );

  const eventsByDate = useMemo(() => {
    const byDate = new Map();

    visibleEvents.forEach(event => {
      const start = event.start?.dateTime || event.start?.date;
      if (!start) return;

      const key = getDateKey(new Date(start));
      const list = byDate.get(key) || [];
      list.push(event);
      byDate.set(key, list);
    });

    byDate.forEach(list => {
      list.sort((a, b) => {
        const aTime = new Date(a.start?.dateTime || a.start?.date).getTime();
        const bTime = new Date(b.start?.dateTime || b.start?.date).getTime();
        return aTime - bTime;
      });
    });

    return byDate;
  }, [visibleEvents]);

  const todoTasks = useMemo(() => tasks.filter(task => task.status !== 'completed'), [tasks]);
  const completedTasks = useMemo(
    () => tasks
      .filter(task => task.status === 'completed')
      .sort((a, b) => new Date(b.completed || b.updated || 0) - new Date(a.completed || a.updated || 0)),
    [tasks]
  );
  const tasksById = useMemo(() => {
    const taskMap = new Map();
    tasks.forEach(task => {
      if (task?.id) taskMap.set(task.id, task);
    });
    return taskMap;
  }, [tasks]);
  const getLinkedTaskForEvent = useCallback((event) => {
    const taskId = getLinkedTaskId(event);
    if (!taskId) return null;
    return tasksById.get(taskId) || null;
  }, [tasksById]);
  const mainCalendarGlowTimersRef = React.useRef([]);
  const mainCalendarGlowFramesRef = React.useRef([]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const clearMainCalendarGlow = useCallback(() => {
    mainCalendarGlowFramesRef.current.forEach(frame => window.cancelAnimationFrame(frame));
    mainCalendarGlowTimersRef.current.forEach(timer => window.clearTimeout(timer));
    mainCalendarGlowFramesRef.current = [];
    mainCalendarGlowTimersRef.current = [];

    document
      .querySelectorAll('.main-calendar-selected-day-glow, .main-calendar-selected-day-surface-glow, .main-calendar-selected-day-cell-glow')
      .forEach(element => {
        element.classList.remove(
          'main-calendar-selected-day-glow',
          'main-calendar-selected-day-surface-glow',
          'main-calendar-selected-day-cell-glow'
        );
      });
  }, []);

  const triggerMainCalendarGlow = useCallback((targetDate) => {
    const dateKey = getDateKey(targetDate);
    clearMainCalendarGlow();

    const firstFrame = window.requestAnimationFrame(() => {
      const secondFrame = window.requestAnimationFrame(() => {
        const targets = document.querySelectorAll(`[data-main-calendar-glow-date="${dateKey}"]`);

        targets.forEach(target => {
          const glowClass = target.dataset.mainCalendarGlowClass;
          if (!glowClass) return;

          target.classList.add(glowClass);
          const timer = window.setTimeout(() => {
            target.classList.remove(glowClass);
          }, 1300);
          mainCalendarGlowTimersRef.current.push(timer);
        });
      });

      mainCalendarGlowFramesRef.current.push(secondFrame);
    });

    mainCalendarGlowFramesRef.current.push(firstFrame);
  }, [clearMainCalendarGlow]);

  useEffect(() => {
    return clearMainCalendarGlow;
  }, [clearMainCalendarGlow]);



  const handleNavigate = (direction) => {
    const next = new Date(currentDate);
    if (view === VIEW_TYPES.MONTH) next.setMonth(currentDate.getMonth() + direction);
    else if (view === VIEW_TYPES.WEEK || view === VIEW_TYPES.SEVEN_DAYS) next.setDate(currentDate.getDate() + (direction * 7));
    else next.setDate(currentDate.getDate() + direction);
    setCurrentDate(next);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setMiniCalDate(today);
    triggerMainCalendarGlow(today);
  };

  const navigateMobileHourlyDay = React.useCallback((direction) => {
    const next = new Date(currentDate);
    next.setDate(currentDate.getDate() + direction);
    setCurrentDate(next);
    setMiniCalDate(new Date(next.getFullYear(), next.getMonth(), 1));
    triggerMainCalendarGlow(next);
  }, [currentDate, triggerMainCalendarGlow]);

  const handleMobileHourlySwipeStart = React.useCallback((e) => {
    if (!isMobileHourlyView || isDragging || e.pointerType !== 'touch') {
      mobileDaySwipeRef.current.tracking = false;
      return;
    }

    mobileDaySwipeRef.current = {
      tracking: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY
    };
  }, [isDragging, isMobileHourlyView]);

  const handleMobileHourlySwipeEnd = React.useCallback((e) => {
    const swipe = mobileDaySwipeRef.current;
    mobileDaySwipeRef.current.tracking = false;

    if (!isMobileHourlyView || !swipe.tracking || e.pointerId !== swipe.pointerId) return;

    const dx = e.clientX - swipe.startX;
    const dy = e.clientY - swipe.startY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const isHorizontalSwipe = (
      absX >= MOBILE_DAY_SWIPE_MIN_X &&
      absY <= MOBILE_DAY_SWIPE_MAX_Y &&
      absX > absY * MOBILE_DAY_SWIPE_DOMINANCE
    );

    if (!isHorizontalSwipe) return;

    e.preventDefault();
    suppressNextEventClickRef.current = true;
    window.clearTimeout(suppressNextEventClickTimeoutRef.current);
    suppressNextEventClickTimeoutRef.current = window.setTimeout(() => {
      suppressNextEventClickRef.current = false;
    }, 300);
    navigateMobileHourlyDay(dx < 0 ? 1 : -1);
  }, [isMobileHourlyView, navigateMobileHourlyDay]);

  const handleMobileHourlySwipeCancel = React.useCallback(() => {
    mobileDaySwipeRef.current = { tracking: false, pointerId: null, startX: 0, startY: 0 };
  }, []);

  const handleCreateEvent = (date, hour) => {
    if (!googleConnected) {
      setShowSettingsModal(true);
      return;
    }

    const startDate = new Date(date);
    // Always create timed events by default (1 hour duration starting at 9 AM if no hour specified)
    if (hour !== undefined) {
      startDate.setHours(hour, 0, 0, 0);
    } else {
      startDate.setHours(9, 0, 0, 0);
    }
    // Create event with dateTime for timed events
    setSelectedEvent({
      start: { dateTime: startDate.toISOString() },
      end: { dateTime: new Date(startDate.getTime() + 60 * 60000).toISOString() }
    });
    setShowEventModal(true);
  };

  // Show quick create popover or fall back to full modal
  const showQuickCreate = useCallback((startDate, endDate, anchorRect) => {
    if (!googleConnected) {
      setShowSettingsModal(true);
      return;
    }

    if (quickCreateEnabled && !isMobile) {
      setQuickCreateData({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        anchorRect
      });
    } else {
      setSelectedEvent({
        start: { dateTime: startDate.toISOString() },
        end: { dateTime: endDate.toISOString() }
      });
      setShowEventModal(true);
    }
  }, [googleConnected, quickCreateEnabled, isMobile]);

  const handleQuickCreateSave = async (title) => {
    if (!quickCreateData || !title.trim()) return;
    try {
      await createEvent({
        summary: title.trim(),
        start: {
          dateTime: quickCreateData.startDate,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        end: {
          dateTime: quickCreateData.endDate,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        colorId: '1'
      });
      setQuickCreateData(null);
      invalidateEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event');
    }
  };

  const handleQuickCreateMoreOptions = () => {
    if (!quickCreateData) return;
    setSelectedEvent({
      start: { dateTime: quickCreateData.startDate },
      end: { dateTime: quickCreateData.endDate }
    });
    setQuickCreateData(null);
    setShowEventModal(true);
  };

  const handleEventClick = (event) => {
    if (suppressNextEventClickRef.current) {
      suppressNextEventClickRef.current = false;
      return;
    }

    setEventContextMenu(null);
    setSelectedEvent(event);
    setShowEventView(true);
  };

  const handleEventContextMenu = (event, e) => {
    e.preventDefault();
    e.stopPropagation();
    const scrollContainer = e.currentTarget.closest('[data-hourly-grid-scroll-key]');
    const scrollKey = scrollContainer?.dataset?.hourlyGridScrollKey;
    if (scrollKey) {
      hourlyGridScrollPositionsRef.current.set(scrollKey, scrollContainer.scrollTop);
    }
    setQuickCreateData(null);
    eventContextMenuOpenedAtRef.current = performance.now();
    suppressNextEventClickRef.current = true;
    window.clearTimeout(suppressNextEventClickTimeoutRef.current);
    suppressNextEventClickTimeoutRef.current = window.setTimeout(() => {
      suppressNextEventClickRef.current = false;
    }, 300);

    const menuWidth = 176;
    const menuHeight = 92;
    setEventContextMenu({
      event,
      x: Math.max(8, Math.min(e.clientX, window.innerWidth - menuWidth - 8)),
      y: Math.max(8, Math.min(e.clientY, window.innerHeight - menuHeight - 8))
    });
  };

  const handleEventViewEdit = (event) => {
    setShowEventView(false);
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const [recurringDeleteEvent, setRecurringDeleteEvent] = useState(null);

  const executeDelete = (event, deleteAll = false) => {
    const marker = buildPendingDeleteMarker(event, deleteAll);
    if (pendingDeleteKeysRef.current.has(marker.key)) return;

    const calendarId = getEventCalendarId(event);
    const eventId = deleteAll && event.recurringEventId ? event.recurringEventId : event.id;

    holdPendingDeleteMarker(marker);
    setEventsData((prev) => prev.filter(ev => !eventMatchesPendingDelete(ev, marker)));

    deleteEvent(eventId, calendarId)
      .then(() => {
        invalidateEvents();
      })
      .catch((error) => {
        console.error('Error deleting event:', error);
        clearPendingDeleteMarker(marker.key);
        invalidateEvents();
        let msg = 'Failed to delete event';
        if (error.response?.status === 403) msg = "You don't have permission to delete this event.";
        else if (error.response?.data?.error) msg = error.response.data.error;
        alert(msg);
      });
  };

  const handleEventViewDelete = (event) => {
    setShowEventView(false);
    setSelectedEvent(null);

    const isRecurring = event.recurringEventId || (event.recurrence && event.recurrence.length > 0);
    if (isRecurring) {
      setRecurringDeleteEvent(event);
    } else {
      executeDelete(event);
    }
  };

  const handleContextMenuEdit = () => {
    if (!eventContextMenu?.event) return;
    const event = eventContextMenu.event;
    setEventContextMenu(null);
    handleEventViewEdit(event);
  };

  const handleContextMenuDelete = () => {
    if (!eventContextMenu?.event) return;
    const event = eventContextMenu.event;
    setEventContextMenu(null);
    handleEventViewDelete(event);
  };

  const handleRecurringDeleteChoice = (choice) => {
    if (recurringDeleteEvent) {
      executeDelete(recurringDeleteEvent, choice === 'all');
    }
    setRecurringDeleteEvent(null);
  };

  // --- Drag-to-create helpers (parent scope so refs survive HourlyGrid remounts) ---
  // Returns fractional hour in 15-min increments (e.g. 9.25 = 9:15)
  const getSlotFromMouseEvent = (e, dayIdx) => {
    const col = columnRefs.current[dayIdx];
    if (!col) return null;
    const rect = col.getBoundingClientRect();
    const y = e.clientY - rect.top;
    // Snap to 15-minute intervals
    return Math.max(0, Math.min(23.75, Math.floor(y / 15) * 0.25));
  };

  const updateDragOverlay = React.useCallback((dc) => {
    const overlay = dragOverlayRef.current;
    if (!overlay) return;
    if (!dc) {
      overlay.style.display = 'none';
      return;
    }
    const col = columnRefs.current[dc.dayIdx];
    if (!col) return;
    overlay.style.display = 'flex';
    overlay.style.top = `${dc.startSlot * 60 + 1}px`;
    overlay.style.height = `${(dc.endSlot - dc.startSlot) * 60 - 3}px`;
    // Position overlay inside the correct column
    const colRect = col.getBoundingClientRect();
    overlay.style.left = `${col.offsetLeft + 4}px`;
    overlay.style.width = `${colRect.width - 8}px`;
    const startH = String(Math.floor(dc.startSlot)).padStart(2, '0');
    const startM = String(Math.round((dc.startSlot % 1) * 60)).padStart(2, '0');
    const endH = String(Math.floor(dc.endSlot)).padStart(2, '0');
    const endM = String(Math.round((dc.endSlot % 1) * 60)).padStart(2, '0');
    overlay.textContent = `${startH}:${startM} – ${endH}:${endM}`;
  }, []);

  // --- Drag-to-move event helpers ---
  const getDayIdxFromMouseEvent = React.useCallback((e, { clamp = false } = {}) => {
    // Find which column the mouse is over
    const entries = Object.entries(columnRefs.current)
      .map(([idx, col]) => [parseInt(idx, 10), col])
      .filter(([, col]) => Boolean(col))
      .sort(([a], [b]) => a - b);

    for (const [idx, col] of entries) {
      if (!col) continue;
      const rect = col.getBoundingClientRect();
      if (e.clientX >= rect.left && e.clientX <= rect.right) {
        return idx;
      }
    }

    if (clamp && entries.length > 0) {
      const first = entries[0];
      const last = entries[entries.length - 1];
      const firstRect = first[1].getBoundingClientRect();
      const lastRect = last[1].getBoundingClientRect();
      if (e.clientX < firstRect.left) return first[0];
      if (e.clientX > lastRect.right) return last[0];

      return entries.reduce((closest, entry) => {
        const [, col] = entry;
        const rect = col.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const closestRect = closest[1].getBoundingClientRect();
        const closestCenter = closestRect.left + closestRect.width / 2;
        return Math.abs(e.clientX - center) < Math.abs(e.clientX - closestCenter) ? entry : closest;
      }, first)[0];
    }

    return null;
  }, []);

  const getSlotFromY = React.useCallback((e, dayIdx) => {
    const col = columnRefs.current[dayIdx];
    if (!col) return null;
    const rect = col.getBoundingClientRect();
    const y = e.clientY - rect.top;
    return Math.max(0, Math.min(23.75, Math.floor(y / 15) * 0.25));
  }, []);

  const getBoundarySlotFromY = React.useCallback((e, dayIdx) => {
    const col = columnRefs.current[dayIdx];
    if (!col) return null;
    const rect = col.getBoundingClientRect();
    const y = e.clientY - rect.top;
    return Math.max(0, Math.min(24, Math.round(y / TIME_GRID_SNAP_MINUTES) * 0.25));
  }, []);

  const getDateFromDayAndSlot = React.useCallback((day, slot) => {
    const date = new Date(day);
    const totalMinutes = Math.round(slot * 60);
    date.setHours(0, totalMinutes, 0, 0);
    return date;
  }, []);

  const getEventUpdateBody = React.useCallback((event, startISO, endISO) => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const eventData = {
      calendarId: getEventCalendarId(event),
      summary: event.summary || '',
      description: event.description || '',
      location: event.location || '',
      start: { dateTime: startISO, timeZone: tz },
      end: { dateTime: endISO, timeZone: tz },
      colorId: event.colorId || undefined,
      recurrence: event.recurrence || undefined,
      reminders: event.reminders || undefined,
      attendees: event.attendees || undefined,
      extendedProperties: event.extendedProperties || undefined,
    };

    Object.keys(eventData).forEach((key) => {
      if (eventData[key] === undefined) delete eventData[key];
    });

    return eventData;
  }, []);

  const applyTimedEventUpdate = React.useCallback((originalEvent, startDate, endDate, errorLabel = 'update event') => {
    const startISO = startDate.toISOString();
    const endISO = endDate.toISOString();

    setEventsData((prev) => prev.map(ev =>
      ev.id === originalEvent.id
        ? { ...ev, start: { ...ev.start, dateTime: startISO }, end: { ...ev.end, dateTime: endISO } }
        : ev
    ));

    updateEvent(originalEvent.id, getEventUpdateBody(originalEvent, startISO, endISO))
      .then(() => {
        invalidateEvents();
      })
      .catch((err) => {
        console.error(`Failed to ${errorLabel}:`, err);
        setEventsData((prev) => prev.map(ev =>
          ev.id === originalEvent.id ? originalEvent : ev
        ));
      });
  }, [getEventUpdateBody, invalidateEvents, setEventsData]);

  const updateDragMoveOverlay = React.useCallback((dm) => {
    const overlay = dragMoveOverlayRef.current;
    if (!overlay) return;
    if (!dm || !dm.started) {
      overlay.style.display = 'none';
      return;
    }
    const col = columnRefs.current[dm.currentDayIdx];
    if (!col) return;
    overlay.style.display = 'flex';
    overlay.style.top = `${dm.currentSlot * 60 + 1}px`;
    overlay.style.height = `${dm.durationSlots * 60 - 3}px`;
    overlay.style.left = `${col.offsetLeft + 4}px`;
    const colRect = col.getBoundingClientRect();
    overlay.style.width = `${colRect.width - 8}px`;
    const startH = String(Math.floor(dm.currentSlot)).padStart(2, '0');
    const startM = String(Math.round((dm.currentSlot % 1) * 60)).padStart(2, '0');
    const endSlot = dm.currentSlot + dm.durationSlots;
    const endH = String(Math.floor(endSlot)).padStart(2, '0');
    const endM = String(Math.round((endSlot % 1) * 60)).padStart(2, '0');
    overlay.querySelector('[data-drag-title]').textContent = dm.event.summary || '';
    overlay.querySelector('[data-drag-time]').textContent = `${startH}:${startM} – ${endH}:${endM}`;
  }, []);

  const handleEventMouseDown = React.useCallback((event, dayIdx, e) => {
    if (e.button !== 0 || isMobile) return;
    e.stopPropagation();
    const eventStart = new Date(event.start.dateTime);
    const startSlot = eventStart.getHours() + eventStart.getMinutes() / 60;
    const eventEnd = new Date(event.end.dateTime);
    const durationMs = eventEnd - eventStart;
    const durationSlots = durationMs / (1000 * 60 * 60);
    // Offset from event top to where mouse grabbed
    const col = columnRefs.current[dayIdx];
    const colRect = col?.getBoundingClientRect();
    const mouseSlot = colRect ? Math.max(0, Math.min(23.75, (e.clientY - colRect.top) / 60)) : startSlot;
    const grabOffset = mouseSlot - startSlot;

    dragMoveRef.current = {
      event,
      originDayIdx: dayIdx,
      originSlot: startSlot,
      durationSlots,
      grabOffset,
      currentDayIdx: dayIdx,
      currentSlot: startSlot,
      startX: e.clientX,
      startY: e.clientY,
      started: false,
    };
  }, [isMobile]);

  const handleEventResizeMouseDown = React.useCallback((event, edge, dayIdx, e) => {
    if (e.button !== 0 || isMobile) return;
    e.preventDefault();
    e.stopPropagation();

    const eventStart = new Date(event.start.dateTime);
    const eventEnd = new Date(event.end.dateTime);
    if (Number.isNaN(eventStart.getTime()) || Number.isNaN(eventEnd.getTime())) return;

    eventResizeRef.current = {
      event,
      edge,
      originDayIdx: dayIdx,
      originalStart: eventStart,
      originalEnd: eventEnd,
      currentStart: eventStart,
      currentEnd: eventEnd,
      startX: e.clientX,
      startY: e.clientY,
      started: false,
    };
  }, [isMobile]);

  const handleColumnMouseDown = (day, dayIdx, e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const slot = getSlotFromMouseEvent(e, dayIdx);
    if (slot === null) return;
    const state = { day, dayIdx, anchorSlot: slot, startSlot: slot, endSlot: slot + 0.25 };
    dragCreateRef.current = state;
    setIsDragging(true);
    updateDragOverlay(state);
  };

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      // Handle drag-to-resize event
      const resize = eventResizeRef.current;
      if (resize) {
        if (!resize.started) {
          const dx = Math.abs(e.clientX - resize.startX);
          const dy = Math.abs(e.clientY - resize.startY);
          if (dx < 5 && dy < 5) return;
          resize.started = true;
          setIsDragging(true);
        }

        const dayIdx = getDayIdxFromMouseEvent(e, { clamp: true });
        if (dayIdx === null) return;
        const slot = getBoundarySlotFromY(e, dayIdx);
        const day = activeDaysRef.current[dayIdx];
        if (slot === null || !day) return;

        const boundary = getDateFromDayAndSlot(day, slot);
        if (resize.edge === 'start') {
          const latestStart = new Date(resize.originalEnd.getTime() - MIN_EVENT_DURATION_MS);
          resize.currentStart = boundary > latestStart ? latestStart : boundary;
          resize.currentEnd = resize.originalEnd;
        } else {
          const earliestEnd = new Date(resize.originalStart.getTime() + MIN_EVENT_DURATION_MS);
          resize.currentStart = resize.originalStart;
          resize.currentEnd = boundary < earliestEnd ? earliestEnd : boundary;
        }

        setEventResizePreview({
          event: resize.event,
          startISO: resize.currentStart.toISOString(),
          endISO: resize.currentEnd.toISOString(),
        });
        return;
      }

      // Handle drag-to-move event
      const dm = dragMoveRef.current;
      if (dm) {
        if (!dm.started) {
          const dx = Math.abs(e.clientX - dm.startX);
          const dy = Math.abs(e.clientY - dm.startY);
          if (dx < 5 && dy < 5) return; // Not enough movement yet
          dm.started = true;
          setIsDragging(true);
        }
        const dayIdx = getDayIdxFromMouseEvent(e);
        if (dayIdx === null) return;
        const slot = getSlotFromY(e, dayIdx);
        if (slot === null) return;
        // Snap the top of the event to where the mouse is, offset by grab point
        const maxStartSlot = dm.durationSlots >= 24 ? 23.75 : 24 - dm.durationSlots;
        const newSlot = Math.max(0, Math.min(maxStartSlot, Math.round((slot - dm.grabOffset) * 4) / 4));
        dm.currentDayIdx = dayIdx;
        dm.currentSlot = newSlot;
        updateDragMoveOverlay(dm);
        return;
      }

      // Handle drag-to-create
      const dc = dragCreateRef.current;
      if (!dc) return;
      const slot = getSlotFromMouseEvent(e, dc.dayIdx);
      if (slot === null) return;
      const newStart = Math.min(dc.anchorSlot, slot);
      const newEnd = Math.max(dc.anchorSlot, slot) + 0.25;
      if (newStart !== dc.startSlot || newEnd !== dc.endSlot) {
        dc.startSlot = newStart;
        dc.endSlot = newEnd;
        updateDragOverlay(dc);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [updateDragOverlay, updateDragMoveOverlay, getDayIdxFromMouseEvent, getSlotFromY, getBoundarySlotFromY, getDateFromDayAndSlot]);

  React.useEffect(() => {
    const handleMouseUp = () => {
      // Handle drag-to-resize event drop
      const resize = eventResizeRef.current;
      if (resize) {
        eventResizeRef.current = null;
        setIsDragging(false);
        setEventResizePreview(null);

        if (resize.started) {
          applyTimedEventUpdate(resize.event, resize.currentStart, resize.currentEnd, 'resize event');
        }
        return;
      }

      // Handle drag-to-move event drop
      const dm = dragMoveRef.current;
      if (dm) {
        dragMoveRef.current = null;
        setIsDragging(false);
        updateDragMoveOverlay(null);
        if (dm.started) {
          // Calculate new start/end from the drop position
          const days = activeDaysRef.current;
          const targetDay = days[dm.currentDayIdx];
          if (targetDay) {
            const newStart = new Date(targetDay);
            const startHour = Math.floor(dm.currentSlot);
            const startMin = Math.round((dm.currentSlot - startHour) * 60);
            newStart.setHours(startHour, startMin, 0, 0);
            const newEnd = new Date(newStart.getTime() + dm.durationSlots * 60 * 60 * 1000);
            const originalEvent = dm.event;

            applyTimedEventUpdate(originalEvent, newStart, newEnd, 'move event');
          }
          return;
        }
        // Not started = it was a click, not a drag — open event view
        handleEventClick(dm.event);
        return;
      }

      const dc = dragCreateRef.current;
      if (dc) {
        dragCreateRef.current = null;
        setIsDragging(false);
        updateDragOverlay(null);
        // If user just clicked (didn't drag) — show quick create for a 1hr event, or skip if disabled
        if (dc.startSlot === dc.anchorSlot && dc.endSlot === dc.anchorSlot + 0.25) {
          if (quickCreateEnabled && !isMobile) {
            const clickStart = new Date(dc.day);
            clickStart.setHours(Math.floor(dc.startSlot), Math.round((dc.startSlot % 1) * 60), 0, 0);
            const clickEnd = new Date(clickStart.getTime() + 60 * 60000);
            const col = columnRefs.current[dc.dayIdx];
            const colRect = col?.getBoundingClientRect();
            const anchorRect = colRect ? {
              top: colRect.top + dc.startSlot * 60,
              left: colRect.left,
              width: colRect.width,
            } : { top: 300, left: 400, width: 200 };
            showQuickCreate(clickStart, clickEnd, anchorRect);
          }
          return;
        }
        const startDate = new Date(dc.day);
        const startHour = Math.floor(dc.startSlot);
        const startMin = Math.round((dc.startSlot - startHour) * 60);
        startDate.setHours(startHour, startMin, 0, 0);
        const endDate = new Date(dc.day);
        const endHour = Math.floor(dc.endSlot);
        const endMin = Math.round((dc.endSlot - endHour) * 60);
        endDate.setHours(endHour, endMin, 0, 0);
        // Get anchor position for quick create popover
        const col = columnRefs.current[dc.dayIdx];
        const colRect = col?.getBoundingClientRect();
        const anchorRect = colRect ? {
          top: colRect.top + dc.startSlot * 60,
          left: colRect.left,
          width: colRect.width,
        } : { top: 300, left: 400, width: 200 };
        showQuickCreate(startDate, endDate, anchorRect);
      }
    };
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [updateDragOverlay, updateDragMoveOverlay, applyTimedEventUpdate, quickCreateEnabled, isMobile, showQuickCreate]);

  const handleAddTask = async () => {
    const title = newTaskText.trim();
    const notes = newTaskNotes.trim();

    if (!title) return;
    if (!googleConnected) {
      setShowSettingsModal(true);
      return;
    }

    const optimisticTaskId = `optimistic-task-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimisticTask = {
      id: optimisticTaskId,
      title,
      status: 'needsAction',
      updated: new Date().toISOString(),
      taskListId: null,
      _optimistic: true,
      ...(notes ? { notes } : {})
    };

    setNewTaskText('');
    setNewTaskNotes('');
    setAddTaskExpanded(false);
    setTasksData(prevTasks => [optimisticTask, ...(prevTasks || [])]);

    try {
      const taskData = { title };
      if (notes) taskData.notes = notes;
      const newTask = await createTask(null, taskData);

      setTasksData(prevTasks => {
        const currentTasks = prevTasks || [];
        const hasOptimisticTask = currentTasks.some(task => task.id === optimisticTaskId);

        if (!hasOptimisticTask) {
          return [newTask, ...currentTasks];
        }

        return currentTasks.map(task =>
          task.id === optimisticTaskId ? newTask : task
        );
      });
    } catch (error) {
      console.error('Error creating task:', error);
      setTasksData(prevTasks => (prevTasks || []).filter(task => task.id !== optimisticTaskId));
      setNewTaskText(title);
      setNewTaskNotes(notes);
      setAddTaskExpanded(Boolean(notes));
    }
  };

  useEffect(() => {
    if (!eventContextMenu) return undefined;

    const closeMenu = () => setEventContextMenu(null);
    const handlePointerDown = (e) => {
      if (e.button !== 0) return;
      if (eventContextMenuRef.current?.contains(e.target)) return;
      closeMenu();
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    const handleScroll = () => {
      if (performance.now() - eventContextMenuOpenedAtRef.current < 300) return;
      closeMenu();
    };

    const listenerFrame = window.requestAnimationFrame(() => {
      window.addEventListener('pointerdown', handlePointerDown, true);
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', closeMenu);
    });

    return () => {
      window.cancelAnimationFrame(listenerFrame);
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [eventContextMenu]);

  useEffect(() => () => {
    window.clearTimeout(suppressNextEventClickTimeoutRef.current);
    pendingDeleteTimersRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
    pendingDeleteTimersRef.current.clear();
    pendingDeleteKeysRef.current.clear();
  }, []);

  const handleToggleTask = async (task) => {
    if (task._optimistic) return;

    if (!googleConnected) {
      setShowSettingsModal(true);
      return;
    }

    try {
      const newStatus = task.status === 'completed' ? 'needsAction' : 'completed';

      // Only send allowed fields to Google Tasks API
      const taskUpdate = {
        id: task.id,
        title: task.title,
        status: newStatus
      };

      // Add optional fields if they exist
      if (task.notes) {
        taskUpdate.notes = task.notes;
      }

      // If completing a task, add animation
      if (newStatus === 'completed') {
        setCompletingTasks(prev => new Set([...prev, task.id]));

        // Wait for animation to complete before updating
        setTimeout(() => {
          updateTask(task.taskListId, task.id, taskUpdate)
            .then(() => {
              // Update tasks state optimistically - move to completed
              setTasksData(prevTasks =>
                prevTasks.map(t =>
                  t.id === task.id ? { ...t, status: 'completed' } : t
                )
              );
            })
            .catch((error) => {
              console.error('Error updating task:', error);
            })
            .finally(() => {
              setCompletingTasks(prev => {
                const next = new Set(prev);
                next.delete(task.id);
                return next;
              });
            });
        }, 1000); // Animation duration - visible tick before removing
      } else {
        // Uncompleting - instant with optimistic update
        await updateTask(task.taskListId, task.id, taskUpdate);
        React.startTransition(() => {
          setTasksData(prevTasks =>
            prevTasks.map(t =>
              t.id === task.id ? { ...t, status: 'needsAction' } : t
            )
          );
        });
      }
    } catch (error) {
      console.error('Error updating task:', error);
      setCompletingTasks(prev => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  };

  const handleToggleLinkedTask = (task, e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!task) return;
    handleToggleTask(task);
  };

  const handleTaskClick = (task) => {
    if (task._optimistic) return;

    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleToggleCalendarVisibility = (calendarId) => {
    setHiddenCalendars(prev => {
      const newSet = new Set(prev);
      if (newSet.has(calendarId)) {
        newSet.delete(calendarId);
      } else {
        newSet.add(calendarId);
      }
      const arr = [...newSet];
      localStorage.setItem('hiddenCalendars', JSON.stringify(arr));
      if (googleConnected) updateSettings({ hiddenCalendars: arr }).catch(() => {});
      return newSet;
    });
  };

  const handleTaskModalClose = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
  };

  const handleTaskSave = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
    invalidateTasks();
  };

  const clearTaskDropHover = useCallback(() => {
    if (!taskDropHoverSlotRef.current) return;

    taskDropHoverSlotRef.current.classList.remove('task-drop-slot-hover');
    taskDropHoverSlotRef.current = null;
  }, []);

  useEffect(() => {
    return clearTaskDropHover;
  }, [clearTaskDropHover]);

  const handleTaskDragStart = (task, e) => {
    if (task._optimistic) return;

    draggedTaskRef.current = task;

    if (e?.dataTransfer) {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', task.title || 'Task');
    }
  };

  const handleTaskDragEnd = () => {
    draggedTaskRef.current = null;
    clearTaskDropHover();
  };

  const handleTimeSlotDragOver = (e) => {
    const draggedTask = draggedTaskRef.current;
    if (!draggedTask) return;

    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';

    if (taskDropHoverSlotRef.current !== e.currentTarget) {
      clearTaskDropHover();
      e.currentTarget.classList.add('task-drop-slot-hover');
      taskDropHoverSlotRef.current = e.currentTarget;
    }
  };

  const handleTimeSlotDragLeave = (e) => {
    if (taskDropHoverSlotRef.current !== e.currentTarget) return;
    clearTaskDropHover();
  };

  const handleTimeSlotDrop = async (date, hour, e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const draggedTask = draggedTaskRef.current;
    console.log('Drop triggered:', { draggedTask, date, hour });
    clearTaskDropHover();

    if (!googleConnected) {
      draggedTaskRef.current = null;
      setShowSettingsModal(true);
      return;
    }

    if (!draggedTask) {
      console.log('No dragged task found');
      return;
    }

    // Create an event from the task
    const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 0, 0, 0);
    const endDate = new Date(startDate.getTime() + 60 * 60000); // 1 hour later

    try {
      console.log('Creating event from task:', draggedTask.title);
      await createEvent({
        summary: draggedTask.title,
        description: draggedTask.notes || '',
        start: {
          dateTime: startDate.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        end: {
          dateTime: endDate.toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        colorId: '1',
        extendedProperties: buildTaskEventExtendedProperties(draggedTask)
      });

      console.log('Event created successfully');
      await invalidateEvents();
    } catch (error) {
      console.error('Error creating event from task:', error);
      alert('Failed to create event from task');
    } finally {
      draggedTaskRef.current = null;
    }
  };

  const monthDays = useMemo(() => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const days = [];
    // Convert Sunday (0) to 7, so Monday becomes 1
    const startDay = start.getDay();
    const startIdx = startDay === 0 ? 6 : startDay - 1;
    for (let i = startIdx; i > 0; i--) {
        days.push({ date: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1 - i), currentMonth: false });
    }
    for (let i = 1; i <= end.getDate(); i++) {
        days.push({ date: new Date(currentDate.getFullYear(), currentDate.getMonth(), i), currentMonth: true });
    }
    while (days.length < 42) {
        days.push({ date: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, days.length - (startIdx + end.getDate()) + 1), currentMonth: false });
    }
    return days;
  }, [currentDate]);

  // Separate mini calendar days based on miniCalDate
  const miniCalMonthDays = useMemo(() => {
    const start = new Date(miniCalDate.getFullYear(), miniCalDate.getMonth(), 1);
    const end = new Date(miniCalDate.getFullYear(), miniCalDate.getMonth() + 1, 0);
    const days = [];
    const startDay = start.getDay();
    const startIdx = startDay === 0 ? 6 : startDay - 1;
    for (let i = startIdx; i > 0; i--) {
        days.push({ date: new Date(miniCalDate.getFullYear(), miniCalDate.getMonth(), 1 - i), currentMonth: false });
    }
    for (let i = 1; i <= end.getDate(); i++) {
        days.push({ date: new Date(miniCalDate.getFullYear(), miniCalDate.getMonth(), i), currentMonth: true });
    }
    while (days.length < 42) {
        days.push({ date: new Date(miniCalDate.getFullYear(), miniCalDate.getMonth() + 1, days.length - (startIdx + end.getDate()) + 1), currentMonth: false });
    }
    return days;
  }, [miniCalDate]);

  const weekDays = useMemo(() => {
    const start = new Date(currentDate);
    // Get day of week (0 = Sunday, 1 = Monday, etc.)
    const day = currentDate.getDay();
    // Calculate offset to Monday (if Sunday, go back 6 days, otherwise go back day-1 days)
    const offset = day === 0 ? -6 : -(day - 1);
    start.setDate(currentDate.getDate() + offset);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [currentDate]);

  const sevenDayRange = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(currentDate);
      d.setDate(currentDate.getDate() + i);
      return d;
    });
  }, [currentDate]);

  const threeDayRange = useMemo(() => {
    return Array.from({ length: 3 }, (_, i) => {
      const d = new Date(currentDate);
      d.setDate(currentDate.getDate() + i);
      return d;
    });
  }, [currentDate]);

  const oneDayRange = useMemo(() => [new Date(currentDate)], [currentDate]);

  const isPastDay = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const getEventsForDay = useCallback(
    (date) => eventsByDate.get(getDateKey(date)) || [],
    [eventsByDate]
  );

  const getEventPosition = (event, currentDay) => {
    const eventStart = new Date(event.start.dateTime || event.start.date);
    const eventEnd = new Date(event.end.dateTime || event.end.date);

    // Get start and end of the current day
    const dayStart = new Date(currentDay);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(currentDay);
    dayEnd.setHours(23, 59, 59, 999);

    // Clip event to current day boundaries
    const clippedStart = eventStart < dayStart ? dayStart : eventStart;
    const clippedEnd = eventEnd > dayEnd ? dayEnd : eventEnd;

    const startMinutes = clippedStart.getHours() * 60 + clippedStart.getMinutes();
    const endMinutes = clippedEnd.getHours() * 60 + clippedEnd.getMinutes();
    const duration = endMinutes - startMinutes;

    return {
      top: startMinutes,
      height: Math.max(duration, 30)
    };
  };

  // Quick Create Popover component
  const QuickCreatePopover = () => {
    const [title, setTitle] = useState('');
    const [saving, setSaving] = useState(false);
    const inputRef = React.useRef(null);
    const popoverRef = React.useRef(null);

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);

    // Close on click outside
    React.useEffect(() => {
      const handleClickOutside = (e) => {
        if (popoverRef.current && !popoverRef.current.contains(e.target)) {
          setQuickCreateData(null);
        }
      };
      const handleEsc = (e) => {
        if (e.key === 'Escape') setQuickCreateData(null);
      };
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEsc);
      };
    }, []);

    if (!quickCreateData) return null;

    const handleSave = async () => {
      if (!title.trim() || saving) return;
      setSaving(true);
      await handleQuickCreateSave(title);
    };

    const start = new Date(quickCreateData.startDate);
    const end = new Date(quickCreateData.endDate);
    const timeStr = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')} – ${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;

    // Position the popover near the anchor
    const anchor = quickCreateData.anchorRect;
    const popoverStyle = {
      position: 'fixed',
      zIndex: 100,
      top: Math.min(anchor.top, window.innerHeight - 200),
      left: Math.min(anchor.left + anchor.width + 8, window.innerWidth - 320),
    };

    // If popover would go off the right edge, position it to the left of the anchor
    if (anchor.left + anchor.width + 320 > window.innerWidth) {
      popoverStyle.left = Math.max(8, anchor.left - 320);
    }

    return (
      <>
        <div className="fixed inset-0 z-[99]" />
        <div
          ref={popoverRef}
          style={popoverStyle}
          className="w-[320px] overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl shadow-zinc-950/10"
        >
          <div className="p-4">
            <Input
              ref={inputRef}
              type="text"
              placeholder="Add title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && title.trim()) handleSave();
              }}
              className="border-0 border-b border-zinc-200 rounded-none px-0 py-2 text-sm font-medium shadow-none focus:border-zinc-950 focus:ring-0"
              disabled={saving}
            />
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-zinc-500">
                {start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
              <span className="text-xs text-zinc-500">{timeStr}</span>
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-zinc-100 bg-zinc-50 px-3 py-2">
            <Button
              plain
              onClick={handleQuickCreateMoreOptions}
              className="px-2 py-1 text-xs"
            >
              More options
            </Button>
            <div className="flex gap-2">
              <Button
                outline
                onClick={() => setQuickCreateData(null)}
                className="px-3 py-1.5 text-xs"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!title.trim() || saving}
                className="px-3 py-1.5 text-xs"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const EventContextMenu = () => {
    if (!eventContextMenu) return null;

    return (
      <div
        ref={eventContextMenuRef}
        role="menu"
        aria-label="Event actions"
        className="fixed z-[120] w-44 overflow-hidden rounded-xl border border-zinc-200 bg-white p-1 shadow-2xl shadow-zinc-950/10 ring-1 ring-zinc-950/5"
        style={{ left: eventContextMenu.x, top: eventContextMenu.y }}
        onPointerDown={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <button
          type="button"
          role="menuitem"
          onClick={handleContextMenuEdit}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-zinc-700 transition hover:bg-zinc-100 hover:text-zinc-950 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Pencil className="h-4 w-4 text-zinc-500" />
          Edit
        </button>
        <button
          type="button"
          role="menuitem"
          onClick={handleContextMenuDelete}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-red-600 transition hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </button>
      </div>
    );
  };

  const GoogleConnectionPrompt = ({ compact = false }) => (
    <div className={`flex-1 flex items-center justify-center ${compact ? 'p-4' : 'p-8'} bg-zinc-50`}>
      <div className="w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-6 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-zinc-950 text-white">
          <CalendarIcon className="h-6 w-6" />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-zinc-950">Connect Google Calendar</h3>
        <p className="mb-5 text-sm text-zinc-500">
          Connect Google to load your calendars, events, and tasks.
        </p>
        <Button
          onClick={() => setShowSettingsModal(true)}
        >
          Open Settings
        </Button>
      </div>
    </div>
  );

  const HourlyGrid = ({ activeDays, initialScroll = 'now' }) => {
    const scrollContainerRef = React.useRef(null);
    const [scrollbarGutterWidth, setScrollbarGutterWidth] = React.useState(0);
    activeDaysRef.current = activeDays;
    const hourlyGridHeight = HOURS.length * 60;
    const hourlyGridContentHeight = hourlyGridHeight + 30;
    const activeDaysKey = activeDays.map(getDateKey).join('|');
    const scrollPositionKey = `${initialScroll}:${activeDaysKey}`;
    const timedEventSource = eventResizePreview
      ? visibleEvents.map(event => (
          event.id === eventResizePreview.event.id
            ? {
                ...event,
                start: { ...event.start, dateTime: eventResizePreview.startISO },
                end: { ...event.end, dateTime: eventResizePreview.endISO },
              }
            : event
        ))
      : visibleEvents;

    const getAllDayEvents = (date) => {
      return (eventsByDate.get(getDateKey(date)) || []).filter(event => !event.start.dateTime);
    };

    const getTimedEvents = (date) => {
      return timedEventSource.filter(event => {
        if (!event.start.dateTime) return false;

        const eventStart = new Date(event.start.dateTime);
        const eventEnd = new Date(event.end.dateTime);

        // Get start and end of the current day
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        // Check if event overlaps with this day at all
        return eventStart < dayEnd && eventEnd > dayStart;
      });
    };

    // Calculate overlap positions for events
    const getEventsWithOverlapInfo = (events, currentDay) => {
      if (events.length === 0) return [];

      // Get positions for all events
      const eventsWithPos = events.map(event => {
        const pos = getEventPosition(event, currentDay);
        return {
          event,
          top: pos.top,
          bottom: pos.top + pos.height,
          height: pos.height
        };
      });

      // Sort by duration (longest first), then by start time
      eventsWithPos.sort((a, b) => {
        // Longest events first (they go in the background at full width)
        if (b.height !== a.height) return b.height - a.height;
        // Then by start time
        if (a.top !== b.top) return a.top - b.top;
        // Then blocked events last
        const aBlocked = isBlockedCalendar(a.event);
        const bBlocked = isBlockedCalendar(b.event);
        if (aBlocked !== bBlocked) return aBlocked ? 1 : -1;
        return 0;
      });

      // Assign layout info based on overlap with longer events
      const result = eventsWithPos.map((item, index) => {
        const isBlocked = isBlockedCalendar(item.event);

        // Find all events that overlap with this one
        const overlapping = eventsWithPos.filter((other, otherIndex) => {
          if (index === otherIndex) return false;
          // Check if they overlap in time
          return item.top < other.bottom && item.bottom > other.top;
        });

        if (overlapping.length === 0) {
          // No overlaps - full width
          return { ...item, width: 'full', columnIndex: 0, zIndex: isBlocked ? 1 : 2 };
        }

        // Blocked events always go behind at full width
        if (isBlocked) {
          return { ...item, width: 'full', columnIndex: 0, zIndex: 1 };
        }

        // Check if this event is the longest among overlapping non-blocked events
        const nonBlockedOverlapping = overlapping.filter(other => !isBlockedCalendar(other.event));
        const isLongest = nonBlockedOverlapping.length === 0 || nonBlockedOverlapping.every(other => item.height >= other.height);

        if (isLongest) {
          // Longest non-blocked event takes full width but sits behind shorter ones
          return { ...item, width: 'full', columnIndex: 0, zIndex: 2 };
        } else {
          // Shorter non-blocked events take 50% width and sit on top to the left
          return { ...item, width: 'half', columnIndex: 0, zIndex: 3 };
        }
      });

      return result;
    };

    // Week-style views open near the end of the day, but preserve manual scroll
    // across context-menu/delete refreshes for the same visible date range.
    React.useLayoutEffect(() => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return undefined;

      const savedScrollTop = hourlyGridScrollPositionsRef.current.get(scrollPositionKey);
      if (savedScrollTop !== undefined) {
        scrollContainer.scrollTop = savedScrollTop;
      } else if (initialScroll === 'end') {
        scrollContainer.scrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
      } else {
        const now = new Date();
        const scrollToHour = Math.max(0, now.getHours() - 1);
        scrollContainer.scrollTop = scrollToHour * 60;
      }

      hourlyGridScrollPositionsRef.current.set(scrollPositionKey, scrollContainer.scrollTop);

      const saveScrollPosition = () => {
        hourlyGridScrollPositionsRef.current.set(scrollPositionKey, scrollContainer.scrollTop);
      };

      scrollContainer.addEventListener('scroll', saveScrollPosition, { passive: true });

      return () => {
        saveScrollPosition();
        scrollContainer.removeEventListener('scroll', saveScrollPosition);
      };
    }, [initialScroll, scrollPositionKey]);

    React.useLayoutEffect(() => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return undefined;

      const updateScrollbarGutter = () => {
        const nextWidth = Math.max(0, scrollContainer.offsetWidth - scrollContainer.clientWidth);
        setScrollbarGutterWidth((currentWidth) => (
          currentWidth === nextWidth ? currentWidth : nextWidth
        ));
      };

      updateScrollbarGutter();

      const resizeObserver = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateScrollbarGutter)
        : null;

      resizeObserver?.observe(scrollContainer);
      window.addEventListener('resize', updateScrollbarGutter);

      return () => {
        resizeObserver?.disconnect();
        window.removeEventListener('resize', updateScrollbarGutter);
      };
    }, [activeDays.length]);

    return (
      <div
        className="flex-1 flex flex-col overflow-hidden"
        style={{ touchAction: isMobileHourlyView ? 'pan-y' : undefined }}
        onPointerDown={handleMobileHourlySwipeStart}
        onPointerUp={handleMobileHourlySwipeEnd}
        onPointerCancel={handleMobileHourlySwipeCancel}
      >
        {/* Date header */}
        <div className="flex shrink-0 border-b border-zinc-200 bg-white">
          <div className="shrink-0 border-r border-zinc-200 bg-white" style={{ width: `${timeColumnWidth}px` }} />
          {activeDays.map((day, i) => {
            const isPast = isPastDay(day);
            const isToday = isSameDay(day, new Date());
            return (
              <div
                key={i}
                data-main-calendar-glow-date={getDateKey(day)}
                data-main-calendar-glow-class="main-calendar-selected-day-surface-glow"
                className={clsx(
                  'flex min-w-[100px] flex-1 items-center justify-between border-r border-zinc-200 bg-zinc-50 px-2 py-1.5',
                  isPast && 'opacity-50'
                )}
              >
                <Badge color={isToday ? 'zinc' : 'blue'} className="px-1.5 py-0.5 text-[10px] uppercase">
                  {DAYS_SHORT[(day.getDay() + 6) % 7]}
                </Badge>
                <div
                  data-main-calendar-glow-date={getDateKey(day)}
                  data-main-calendar-glow-class="main-calendar-selected-day-glow"
                  className={clsx(
                    'flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold',
                    isToday ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-950'
                  )}
                >
                  {day.getDate()}
                </div>
              </div>
            );
          })}
          <div
            aria-hidden="true"
            className="shrink-0 bg-white"
            style={{ width: `${scrollbarGutterWidth}px` }}
          />
        </div>

        {/* All-day events */}
        <div className="flex shrink-0 border-b border-zinc-200 bg-zinc-50">
          <div className="flex shrink-0 items-center justify-center border-r border-zinc-200 px-1 py-2" style={{ width: `${timeColumnWidth}px` }}>
            {secondaryTimezone ? (
              <div className="flex w-full justify-between gap-1 text-[9px] font-semibold leading-tight text-zinc-500">
                <span className="opacity-70">{secondaryTimezoneAbbr}</span>
                <span>{localTimezoneAbbr}</span>
              </div>
            ) : (
              <Badge color="zinc" className="px-1.5 py-0.5 text-[9px] uppercase">All day</Badge>
            )}
          </div>
          {activeDays.map((day, dayIdx) => {
            const allDayEvents = getAllDayEvents(day);
            const isPast = isPastDay(day);
            return (
              <div
                key={dayIdx}
                className={clsx(
                  'min-h-[36px] min-w-[100px] flex-1 border-r border-zinc-200 bg-zinc-50 p-1',
                  isPast && 'opacity-50'
                )}
              >
                {allDayEvents.map((event) => {
                  const linkedTask = getLinkedTaskForEvent(event);
                  return (
                    <CalendarEventBadge
                      key={event.id}
                      onClick={(e) => { e.stopPropagation(); handleEventClick(event); }}
                      onContextMenu={(e) => handleEventContextMenu(event, e)}
                      event={event}
                      linkedTask={linkedTask}
                      linkedTaskCompleting={linkedTask ? completingTasks.has(linkedTask.id) : false}
                      onToggleLinkedTask={(e) => handleToggleLinkedTask(linkedTask, e)}
                    />
                  );
                })}
                {allDayEvents.length === 0 && (
                  <div style={{ height: '100%', minHeight: '32px', cursor: 'default' }} onClick={() => handleCreateEvent(day)} />
                )}
              </div>
            );
          })}
          <div
            aria-hidden="true"
            className="shrink-0 bg-zinc-50"
            style={{ width: `${scrollbarGutterWidth}px` }}
          />
        </div>

        {/* Scrollable hourly grid */}
        <div
          ref={scrollContainerRef}
          data-hourly-grid-scroll-key={scrollPositionKey}
          className="custom-scrollbar flex flex-1 overflow-y-auto bg-white"
        >
          {/* Time labels column */}
          <div
            className="shrink-0 border-r border-zinc-200 bg-zinc-50"
            style={{
              width: `${timeColumnWidth}px`,
              minHeight: `${hourlyGridContentHeight}px`
            }}
          >
            {HOURS.map(h => (
              <div
                key={h}
                className="relative h-[60px]"
              >
                {secondaryTimezone ? (
                  <div className="absolute -top-2.5 left-0 right-0 flex select-none justify-between gap-1 bg-zinc-50 px-0.5 text-[9px] font-medium text-zinc-500">
                    <span className="opacity-70">{getHourInTimezone(h, secondaryTimezone)}</span>
                    <span>{String(h).padStart(2, '0')}:00</span>
                  </div>
                ) : (
                  <span className="absolute -top-2.5 right-1 select-none bg-zinc-50 px-1 text-[9px] font-medium text-zinc-500">
                    {String(h).padStart(2, '0')}:00
                  </span>
                )}
              </div>
            ))}
            {/* 00:00 label at bottom */}
            <div className="relative h-[30px]">
              {secondaryTimezone ? (
                <div className="absolute -top-2.5 left-0 right-0 flex select-none justify-between gap-1 bg-zinc-50 px-0.5 text-[9px] font-medium text-zinc-500">
                  <span className="opacity-70">{getHourInTimezone(0, secondaryTimezone)}</span>
                  <span>00:00</span>
                </div>
              ) : (
                <span className="absolute -top-2.5 right-1 select-none bg-zinc-50 px-1 text-[9px] font-medium text-zinc-500">
                  00:00
                </span>
              )}
            </div>
          </div>

          {/* Grid area with background lines */}
          <div className="relative flex flex-1" style={{ minHeight: `${hourlyGridContentHeight}px` }}>
            {/* Horizontal lines layer */}
            <div className="pointer-events-none absolute inset-x-0 top-0 z-0" style={{ height: `${hourlyGridHeight}px` }}>
              {HOURS.map(h => (
                <div
                  key={h}
                  className="h-[60px] border-b border-zinc-200"
                />
              ))}
            </div>

            {/* Vertical lines layer */}
            <div className="pointer-events-none absolute inset-x-0 top-0 z-0 flex" style={{ height: `${hourlyGridHeight}px` }}>
              {activeDays.map((_, dayIdx) => (
                <div
                  key={dayIdx}
                  className="h-full min-w-[100px] flex-1 border-r border-zinc-200"
                />
              ))}
            </div>

            {/* Day columns for content and interaction */}
            {activeDays.map((day, dayIdx) => {
              const isPast = isPastDay(day);
              const timedEvents = getTimedEvents(day);
              return (
                <div
                  key={dayIdx}
                  ref={(el) => { columnRefs.current[dayIdx] = el; }}
                  className={clsx(
                    'relative z-[1] min-w-[100px] flex-1 select-none',
                    isPast && 'opacity-50'
                  )}
                  style={{
                    cursor: 'default'
                  }}
                  onMouseDown={(e) => handleColumnMouseDown(day, dayIdx, e)}
                >
                  {/* Hour cells (visual grid + drop targets + tap to create) */}
                  {HOURS.map(h => {
                    const slotId = `${day.toISOString()}-${h}`;
                    const isTapped = tappedSlot === slotId;
                    return (
                      <div
                        key={h}
                        className={clsx(
                          'task-drop-slot h-[60px] transition-colors duration-150',
                          isTapped && 'rounded-md bg-blue-100 shadow-[inset_0_0_0_2px_rgba(59,130,246,0.45)]'
                        )}
                        style={{
                          boxSizing: 'border-box',
                          pointerEvents: isDragging ? 'none' : 'auto',
                        }}
                        onTouchStart={(e) => {
                          // Record touch start position to distinguish taps from scrolls
                          const touch = e.touches[0];
                          e.currentTarget.dataset.touchStartX = touch.clientX;
                          e.currentTarget.dataset.touchStartY = touch.clientY;
                        }}
                        onTouchEnd={(e) => {
                          // Only create event on genuine tap, not scroll
                          const touch = e.changedTouches[0];
                          const startX = parseFloat(e.currentTarget.dataset.touchStartX);
                          const startY = parseFloat(e.currentTarget.dataset.touchStartY);
                          const dx = Math.abs(touch.clientX - startX);
                          const dy = Math.abs(touch.clientY - startY);
                          if (dx > TOUCH_TAP_MOVE_THRESHOLD || dy > TOUCH_TAP_MOVE_THRESHOLD) return; // finger moved — it was a scroll
                          e.preventDefault();
                          // Freeze scroll position so modal opening doesn't cause a jump
                          const scrollContainer = scrollContainerRef.current;
                          const savedScroll = scrollContainer ? scrollContainer.scrollTop : 0;
                          setTappedSlot(slotId);
                          const startDate = new Date(day);
                          startDate.setHours(h, 0, 0, 0);
                          const endDate = new Date(day);
                          endDate.setHours(h + 1, 0, 0, 0);
                          setTimeout(() => {
                            // Restore scroll before opening modal
                            if (scrollContainer) scrollContainer.scrollTop = savedScroll;
                            setTappedSlot(null);
                            setSelectedEvent({
                              start: { dateTime: startDate.toISOString() },
                              end: { dateTime: endDate.toISOString() }
                            });
                            setShowEventModal(true);
                          }, 300);
                        }}
                        onDragOver={handleTimeSlotDragOver}
                        onDragLeave={handleTimeSlotDragLeave}
                        onDrop={(e) => handleTimeSlotDrop(day, h, e)}
                      />
                    );
                  })}

                  {/* Events overlay */}
                  {getEventsWithOverlapInfo(timedEvents, day).map(({ event, top, height, width: widthType, zIndex }) => {
                    const dimmed = isEventDimmed(event);
                    const { tone } = getEventColors(event, dimmed);
                    const isBlocked = isBlockedCalendar(event);
                    const linkedTask = getLinkedTaskForEvent(event);
                    const linkedTaskCompleting = linkedTask ? completingTasks.has(linkedTask.id) : false;
                    const linkedTaskChecked = isLinkedTaskChecked(linkedTask, linkedTaskCompleting);
                    const canResizeEvent = !isMobile && Boolean(event.start?.dateTime && event.end?.dateTime);
                    return (
                      <div
                        role="button"
                        tabIndex={0}
                        key={event.id}
                        className={clsx(
                          'group absolute overflow-hidden rounded-lg border px-2 py-1 text-left shadow-sm transition',
                          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
                          tone.event,
                          (isBlocked || dimmed) && 'opacity-60',
                          isMobile ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'
                        )}
                        style={{
                          top: `${top + 1}px`,
                          left: '4px',
                          width: widthType === 'half' ? 'calc(50% - 4px)' : 'calc(100% - 8px)',
                          height: `${height - 3}px`,
                          zIndex: zIndex + 10,
                          pointerEvents: isDragging ? 'none' : 'auto',
                        }}
                        onMouseDown={(e) => handleEventMouseDown(event, dayIdx, e)}
                        onContextMenu={(e) => handleEventContextMenu(event, e)}
                        onKeyDown={(e) => handleKeyboardClick(e, (keyboardEvent) => {
                          keyboardEvent.stopPropagation();
                          handleEventClick(event);
                        })}
                        onTouchStart={(e) => {
                          if (!isMobile) return;
                          const touch = e.touches[0];
                          e.currentTarget.dataset.touchStartX = touch.clientX;
                          e.currentTarget.dataset.touchStartY = touch.clientY;
                        }}
                        onTouchEnd={(e) => {
                          if (!isMobile) return;
                          const touch = e.changedTouches[0];
                          const startX = parseFloat(e.currentTarget.dataset.touchStartX);
                          const startY = parseFloat(e.currentTarget.dataset.touchStartY);
                          const dx = Math.abs(touch.clientX - startX);
                          const dy = Math.abs(touch.clientY - startY);

                          if (!Number.isFinite(dx) || !Number.isFinite(dy) || dx > TOUCH_TAP_MOVE_THRESHOLD || dy > TOUCH_TAP_MOVE_THRESHOLD) {
                            suppressNextEventClickRef.current = true;
                            window.clearTimeout(suppressNextEventClickTimeoutRef.current);
                            suppressNextEventClickTimeoutRef.current = window.setTimeout(() => {
                              suppressNextEventClickRef.current = false;
                            }, 300);
                            return;
                          }

                          e.stopPropagation();
                          e.preventDefault();
                          handleEventClick(event);
                        }}
                        onClick={(e) => { e.stopPropagation(); if (isMobile) handleEventClick(event); }}
                      >
                        {canResizeEvent && (
                          <>
                            <button
                              type="button"
                              aria-label={`Adjust start time for ${event.summary || 'event'}`}
                              title="Adjust start time"
                              className="absolute inset-x-0 top-0 z-20 flex h-2 cursor-ns-resize items-start justify-center opacity-0 transition group-hover:opacity-100 focus:opacity-100"
                              onMouseDown={(e) => handleEventResizeMouseDown(event, 'start', dayIdx, e)}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <span className="mt-0.5 h-0.5 w-8 rounded-full bg-white/80 shadow-sm" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Adjust end time for ${event.summary || 'event'}`}
                              title="Adjust end time"
                              className="absolute inset-x-0 bottom-0 z-20 flex h-2 cursor-ns-resize items-end justify-center opacity-0 transition group-hover:opacity-100 focus:opacity-100"
                              onMouseDown={(e) => handleEventResizeMouseDown(event, 'end', dayIdx, e)}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <span className="mb-0.5 h-0.5 w-8 rounded-full bg-white/80 shadow-sm" />
                            </button>
                          </>
                        )}
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex min-w-0 flex-1 items-start gap-1.5">
                            {linkedTask && (
                              <LinkedTaskCheckmark
                                checked={linkedTaskChecked}
                                compact={height < 34}
                                onToggle={(e) => handleToggleLinkedTask(linkedTask, e)}
                                className="mt-0.5"
                              />
                            )}
                            <div className={clsx('min-w-0 flex-1 truncate text-xs font-semibold leading-4', linkedTaskChecked && 'line-through opacity-75')}>
                              {event.summary || '(No title)'}
                            </div>
                          </div>
                          {hasVideoConference(event) && <Video className="h-3 w-3 shrink-0 opacity-90" />}
                        </div>
                        {height > 40 && event.start?.dateTime && event.end?.dateTime && (
                          <div className="mt-0.5 truncate text-[10px] font-medium opacity-80">
                            {formatEventTimeRange(event)}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Travel time blocks (iOS style) */}
                  {timedEvents.map(event => {
                    const travel = getTravelTime(event);
                    if (!travel.before && !travel.after) return null;
                    const pos = getEventPosition(event, day);
                    const { tone } = getEventColors(event, false);
                    return (
                      <React.Fragment key={`travel-${event.id}`}>
                        {travel.before > 0 && (() => {
                          const blockHeight = Math.min(travel.before, pos.top);
                          return (
                            <div
                              className={clsx(
                                'absolute left-1 z-[9] flex overflow-hidden rounded-t-lg border border-b-0 text-[11px] font-medium',
                                tone.travel
                              )}
                              style={{
                                top: `${Math.max(0, pos.top - travel.before)}px`,
                                width: 'calc(100% - 8px)',
                                height: `${blockHeight}px`,
                                pointerEvents: 'none',
                              }}
                            >
                              {/* Dotted connector line on left */}
                              <div className="flex w-4 shrink-0 flex-col items-center justify-center">
                                {[...Array(Math.max(1, Math.floor(blockHeight / 6)))].map((_, i) => (
                                  <div
                                    key={i}
                                    className={clsx('h-1 w-1 rounded-full opacity-60', tone.dot)}
                                    style={{ marginBottom: i < Math.floor(blockHeight / 6) - 1 ? '3px' : 0 }}
                                  />
                                ))}
                              </div>
                              {/* Content */}
                              <div className="flex min-w-0 flex-1 items-center gap-1.5 opacity-75">
                                <Car className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate whitespace-nowrap">
                                  {formatTravelDuration(travel.before)} travel time
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                        {travel.after > 0 && (
                          <div
                            className={clsx(
                              'absolute left-1 z-[9] flex overflow-hidden rounded-b-lg border border-t-0 text-[11px] font-medium',
                              tone.travel
                            )}
                            style={{
                              top: `${pos.top + pos.height}px`,
                              width: 'calc(100% - 8px)',
                              height: `${travel.after}px`,
                              pointerEvents: 'none',
                            }}
                          >
                            {/* Dotted connector line on left */}
                            <div className="flex w-4 shrink-0 flex-col items-center justify-center">
                              {[...Array(Math.max(1, Math.floor(travel.after / 6)))].map((_, i) => (
                                <div
                                  key={i}
                                  className={clsx('h-1 w-1 rounded-full opacity-60', tone.dot)}
                                  style={{ marginBottom: i < Math.floor(travel.after / 6) - 1 ? '3px' : 0 }}
                                />
                              ))}
                            </div>
                            {/* Content */}
                            <div className="flex min-w-0 flex-1 items-center gap-1.5 opacity-75">
                              <Car className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate whitespace-nowrap">
                                {formatTravelDuration(travel.after)} travel time
                              </span>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Ghost event while quick create popover is open */}
                  {quickCreateData && (() => {
                    const ghostStart = new Date(quickCreateData.startDate);
                    if (!isSameDay(ghostStart, day)) return null;
                    const ghostEnd = new Date(quickCreateData.endDate);
                    const ghostTop = ghostStart.getHours() * 60 + ghostStart.getMinutes();
                    const ghostHeight = Math.max((ghostEnd - ghostStart) / 60000, 30);
                    const { tone } = getEventColors({ colorId: '1' }, false);
                    return (
                      <div
                        className={clsx(
                          'absolute left-1 z-20 rounded-lg border-2 px-2 py-1 text-[11px] font-semibold shadow-sm',
                          tone.ghost
                        )}
                        style={{
                          top: `${ghostTop + 1}px`,
                          width: 'calc(100% - 8px)',
                          height: `${ghostHeight - 3}px`,
                          pointerEvents: 'none',
                        }}
                      >
                        {`${String(ghostStart.getHours()).padStart(2, '0')}:${String(ghostStart.getMinutes()).padStart(2, '0')} – ${String(ghostEnd.getHours()).padStart(2, '0')}:${String(ghostEnd.getMinutes()).padStart(2, '0')}`}
                      </div>
                    );
                  })()}

                  {/* Current time indicator */}
                  {isSameDay(day, now) && (
                    <div className="pointer-events-none absolute z-20 flex w-full items-center" style={{ top: `${(now.getHours() * 60) + now.getMinutes()}px` }}>
                      <div className="-ml-1.5 h-3 w-3 rounded-full bg-rose-500 shadow-sm shadow-rose-950/20" />
                      <div className="h-0.5 flex-1 bg-rose-500" />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Drag-to-create overlay — positioned via ref, no React re-renders */}
            <div
              ref={dragOverlayRef}
              className="absolute z-[25] hidden items-start rounded-lg border-2 border-blue-500 bg-blue-50/90 px-2 py-1 text-[11px] font-semibold text-blue-700 shadow-sm"
              style={{
                pointerEvents: 'none',
              }}
            />

            {/* Drag-to-move event ghost overlay */}
            <div
              ref={dragMoveOverlayRef}
              className="absolute z-30 hidden flex-col overflow-hidden rounded-lg border border-blue-700/20 bg-blue-600 px-2 py-1 text-left text-xs font-semibold text-white shadow-lg shadow-blue-950/25"
              style={{
                pointerEvents: 'none',
              }}
            >
              <div data-drag-title="" className="truncate" />
              <div data-drag-time="" className="mt-0.5 text-[10px] opacity-80" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Mobile Events List Component - Infinite scroll grouped by day
  const mobileEventsListRef = React.useRef(null);
  const dayRefs = React.useRef({});

  // Group events by day for the next 60 days
  const groupedEvents = useMemo(() => {
    const groups = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Show 30 days before and 60 days after today
    for (let i = -30; i < 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const dayEvents = eventsByDate.get(getDateKey(date)) || [];

      groups.push({ date, events: dayEvents });
    }
    return groups;
  }, [eventsByDate]);

  // Scroll to selected date when it changes - smooth custom scroll
  const scrollToDate = React.useCallback((targetDate, instant = false) => {
    const dateKey = targetDate.toDateString();
    const element = dayRefs.current[dateKey];
    const container = mobileEventsListRef.current;
    if (element && container) {
      const elementRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const targetScroll = container.scrollTop + (elementRect.top - containerRect.top);

      if (instant) {
        container.scrollTop = targetScroll;
        return;
      }

      // Smooth scroll with easing
      const startScroll = container.scrollTop;
      const distance = targetScroll - startScroll;
      const duration = 350; // ms
      let startTime = null;

      const easeOutQuad = (t) => t * (2 - t);

      const animateScroll = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutQuad(progress);

        container.scrollTop = startScroll + (distance * eased);

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  }, []);

  const handleMiniCalendarDateSelect = React.useCallback((targetDate, options = {}) => {
    const selectedDate = new Date(targetDate);

    setCurrentDate(selectedDate);
    triggerMainCalendarGlow(selectedDate);

    if (options.syncMiniMonth) {
      setMiniCalDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    }

    if (options.scrollMobileList) {
      window.setTimeout(() => scrollToDate(selectedDate), 50);
    }
  }, [scrollToDate, triggerMainCalendarGlow]);

  const goToMobileToday = React.useCallback(() => {
    const today = new Date();
    setCurrentDate(today);
    setMiniCalDate(new Date(today.getFullYear(), today.getMonth(), 1));
    triggerMainCalendarGlow(today);
    window.setTimeout(() => scrollToDate(today), 50);
  }, [scrollToDate, triggerMainCalendarGlow]);

  // Track if initial scroll has happened
  const hasInitialScrolled = React.useRef(false);

  // Scroll to today on initial load and whenever returning to calendar tab
  React.useEffect(() => {
    if (isMobile && mobileTab === 'calendar' && !loading && groupedEvents.length > 0) {
      const scrollTimeout = setTimeout(() => {
        const today = new Date();
        const dateKey = today.toDateString();
        if (dayRefs.current[dateKey]) {
          hasInitialScrolled.current = true;
          scrollToDate(today, true); // Always instant — no visible scroll animation
        }
      }, 50);

      return () => clearTimeout(scrollTimeout);
    }
  }, [isMobile, mobileTab, scrollToDate, loading, groupedEvents]);

  // Mobile View
  if (isMobile) {
    return (
      <div className="fixed inset-0 flex flex-col bg-zinc-100 font-sans text-zinc-950">
        {/* Mobile Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden" style={{ display: mobileTab === 'calendar' ? 'flex' : 'none' }}>
              {/* Mini Calendar Header - Fixed */}
              <div className={clsx(
                'flex-shrink-0 border-b border-zinc-200 bg-white px-4 pt-4 shadow-sm transition-all duration-300 ease-out',
                mobileMiniCalendarExpanded ? 'pb-3' : 'pb-2'
              )}>
                <div className={clsx(
                  'flex items-center justify-between transition-all duration-300 ease-out',
                  mobileMiniCalendarExpanded ? 'mb-2' : 'mb-0'
                )}>
                  <span className="text-lg font-semibold text-zinc-950">{miniCalDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                  <div className="flex gap-1">
                    <Button plain className="h-8 w-8 rounded-full p-0" onClick={() => setMiniCalDate(new Date(miniCalDate.getFullYear(), miniCalDate.getMonth() - 1))} aria-label="Previous month">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button plain className="h-8 w-8 rounded-full p-0" onClick={() => setMiniCalDate(new Date(miniCalDate.getFullYear(), miniCalDate.getMonth() + 1))} aria-label="Next month">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div
                  aria-hidden={!mobileMiniCalendarExpanded}
                  className={clsx(
                    'overflow-hidden transition-all duration-300 ease-out',
                    mobileMiniCalendarExpanded
                      ? 'max-h-44 translate-y-0 opacity-100'
                      : 'pointer-events-none max-h-0 -translate-y-3 opacity-0'
                  )}
                >
                  <div className="mb-0.5 grid grid-cols-7 text-center text-xs font-bold text-zinc-500">
                    {DAYS_SHORT.map((d, i) => <div key={i}>{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 text-center text-sm">
                    {miniCalMonthDays.slice(0, 35).map((d, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          const targetDate = d.date;
                          handleMiniCalendarDateSelect(targetDate, {
                            syncMiniMonth: !d.currentMonth,
                            scrollMobileList: true
                          });
                        }}
                        className={`py-1 cursor-pointer rounded-full transition-colors ${
                          isSameDay(d.date, currentDate)
                            ? 'bg-zinc-950 text-white font-bold'
                            : isSameDay(d.date, new Date())
                            ? 'bg-blue-50 text-blue-700 font-bold'
                            : !d.currentMonth
                            ? 'text-zinc-300'
                            : 'text-zinc-700 hover:bg-zinc-100'
                        }`}
                      >
                        {d.date.getDate()}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* View Selector Bar */}
              <div className="flex-shrink-0 flex items-center gap-2 border-b border-zinc-200 bg-white px-4 py-2">
                <Button
                  onClick={() => {
                    const wasHourlyView = mobileCalendarView === '3day' || mobileCalendarView === '1day';
                    setMobileCalendarView('month');
                    if (wasHourlyView) {
                      goToMobileToday();
                    }
                  }}
                  outline={mobileCalendarView !== 'month'}
                  color={mobileCalendarView === 'month' ? 'dark/zinc' : 'white'}
                  className="px-3 py-1.5"
                >
                  Month
                </Button>
                <Button
                  onClick={() => setMobileCalendarView('3day')}
                  outline={mobileCalendarView !== '3day'}
                  color={mobileCalendarView === '3day' ? 'dark/zinc' : 'white'}
                  className="px-2.5 py-1.5"
                >
                  3 Day
                </Button>
                <Button
                  onClick={() => {
                    const today = new Date();
                    setCurrentDate(today);
                    setMiniCalDate(new Date(today.getFullYear(), today.getMonth(), 1));
                    setMobileCalendarView('1day');
                    triggerMainCalendarGlow(today);
                  }}
                  outline={mobileCalendarView !== '1day'}
                  color={mobileCalendarView === '1day' ? 'dark/zinc' : 'white'}
                  className="px-2.5 py-1.5"
                >
                  1 Day
                </Button>
                <Button
                  outline
                  onClick={goToMobileToday}
                  className="ml-auto px-3 py-1.5"
                >
                  Today
                </Button>
              </div>

              {/* Content Area - Month View or 3 Day View */}
              {!canRenderCalendar ? (
                <GoogleConnectionPrompt compact />
              ) : googleConnected && loading ? (
                <div className="flex-1 overflow-hidden p-3 space-y-4">
                  {[...Array(3)].map((_, gi) => (
                    <div key={gi}>
                      <div className="h-4 w-32 animate-pulse rounded bg-zinc-200 mb-3" />
                      <div className="space-y-3">
                        {[...Array(gi === 0 ? 3 : 2)].map((_, i) => (
                          <div key={i} className="rounded-lg border border-zinc-200 bg-white px-3 py-4 shadow-sm">
                            <div className="flex justify-between items-start">
                              <div className="h-4 w-40 animate-pulse rounded bg-zinc-200" />
                              <div className="flex flex-col items-end gap-1">
                                <div className="h-3 w-10 animate-pulse rounded bg-zinc-200" />
                                <div className="h-3 w-10 animate-pulse rounded bg-zinc-200 opacity-50" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : mobileCalendarView === '3day' || mobileCalendarView === '1day' ? (
                <HourlyGrid activeDays={mobileCalendarView === '1day' ? oneDayRange : threeDayRange} />
              ) : (
                <div ref={mobileEventsListRef} className="flex-1 overflow-y-auto">
                  {groupedEvents.map(({ date, events: dayEvents }) => {
                    const dateKey = date.toDateString();
                    const isToday = isSameDay(date, new Date());
                    const isSelected = isSameDay(date, currentDate);
                    const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

                    return (
                      <div
                        key={dateKey}
                        ref={(el) => { dayRefs.current[dateKey] = el; }}
                        className={`${isPast && !isToday ? 'opacity-50' : ''}`}
                      >
                        {/* Day Header */}
                        <div
                          data-main-calendar-glow-date={dateKey}
                          data-main-calendar-glow-class="main-calendar-selected-day-surface-glow"
                          className="sticky top-0 z-10 flex items-center gap-2 border-b border-zinc-200 bg-white/95 px-3 py-2 backdrop-blur"
                        >
                          <Badge color={isToday ? 'zinc' : isSelected ? 'blue' : 'zinc'} className="uppercase">
                            {isToday ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short' })}
                          </Badge>
                          <span className="text-xs font-semibold uppercase text-zinc-500">
                            {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>

                        {/* Events for this day */}
                        <div className="p-3 space-y-3">
                          {dayEvents.length === 0 ? (
                            <div className="flex justify-center py-3">
                              <Badge color="zinc" className="text-[11px]">No events</Badge>
                            </div>
                          ) : (
                            dayEvents.map(event => {
                              const dimmed = isEventDimmed(event);
                              const eventEnd = event.end?.dateTime ? new Date(event.end.dateTime) : null;
                              const isPastEvent = isToday && eventEnd && eventEnd < now;
                              const linkedTask = getLinkedTaskForEvent(event);
                              return (
                                <CalendarListEventCard
                                  key={event.id}
                                  event={event}
                                  dimmed={dimmed}
                                  past={isPastEvent}
                                  linkedTask={linkedTask}
                                  linkedTaskCompleting={linkedTask ? completingTasks.has(linkedTask.id) : false}
                                  onToggleLinkedTask={(e) => handleToggleLinkedTask(linkedTask, e)}
                                  onClick={() => handleEventClick(event)}
                                  onContextMenu={(e) => handleEventContextMenu(event, e)}
                                />
                              );
                            })
                          )}
                        </div>
                        {/* Spacer after today: push next day just off-screen with 20px peek */}
                        {isToday && (
                          <div
                            ref={(el) => {
                              if (!el) return;
                              const measure = () => {
                                const container = mobileEventsListRef.current;
                                if (!container) return;
                                const containerHeight = container.clientHeight;
                                const dayEl = el.parentElement;
                                const contentHeight = el.offsetTop - dayEl.offsetTop;
                                const needed = containerHeight - contentHeight + 20;
                                el.style.height = needed > 0 ? `${needed}px` : '0px';
                              };
                              measure();
                              requestAnimationFrame(measure);
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          <div className="flex-1 flex flex-col overflow-hidden" style={{ display: mobileTab === 'todo' ? 'flex' : 'none' }}>
              {/* Header - Fixed */}
              <div className="flex-shrink-0 border-b border-zinc-200 bg-white p-4 shadow-sm">
                <h1 className="flex items-center gap-2 text-lg font-semibold text-zinc-950">
                  <CheckSquare className="h-5 w-5 text-zinc-500" />
                  My Tasks
                </h1>
              </div>

              {/* Add Task */}
              <div className="relative flex-shrink-0 border-b border-zinc-200 bg-white" style={{ zIndex: addTaskExpanded ? 20 : 1 }}>
                <div className="p-3 pb-0">
                  <div className="relative">
                    <Input
                      ref={taskInputRef}
                      type="text"
                      placeholder="Add a task"
                      value={newTaskText}
                      onChange={(e) => setNewTaskText(e.target.value)}
                      onFocus={() => setAddTaskExpanded(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTask();
                        }
                      }}
                      className="bg-zinc-50 pl-9 text-base"
                    />
                    <Plus className="absolute left-3 top-3.5 h-4 w-4 cursor-pointer text-zinc-500" onClick={() => setAddTaskExpanded(!addTaskExpanded)} />
                  </div>
                </div>
                <div style={{ height: '12px' }} />
                {/* Overlay details panel */}
                {addTaskExpanded && (
                  <>
                    <div
                      style={{ position: 'absolute', top: '100%', left: 0, right: 0, height: '100vh', zIndex: 18, backgroundColor: 'rgba(0,0,0,0.15)' }}
                      onClick={() => { setAddTaskExpanded(false); setNewTaskNotes(''); }}
                    />
                    <div
                      className="absolute left-0 right-0 bg-white shadow-2xl shadow-zinc-950/10"
                      style={{
                        zIndex: 25,
                        top: '100%',
                        borderTop: '1px solid #E4E4E7',
                        borderRadius: '0 0 16px 16px',
                        animation: 'slideDownPanel 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="px-3 pt-3 pb-3">
                        <Textarea
                          placeholder="Add details..."
                          value={newTaskNotes}
                          onChange={(e) => setNewTaskNotes(e.target.value)}
                          className="bg-zinc-50"
                          rows={4}
                          resizable={false}
                        />
                        <div className="flex items-center justify-between mt-2">
                          <Button
                            outline
                            onClick={() => { setAddTaskExpanded(false); setNewTaskNotes(''); }}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleAddTask}
                          >
                            Add
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Tasks List - Scrollable */}
              <div className="flex-1 overflow-y-auto bg-zinc-100 p-3" style={{ position: 'relative', zIndex: addTaskExpanded ? 0 : 1 }}>
                <div className="mb-2 text-xs font-semibold uppercase text-zinc-500">
                  To do ({todoTasks.length})
                </div>
                <div className="space-y-2">
                  {todoTasks.map(task => {
                    const isCompleting = completingTasks.has(task.id);
                    return (
                      <div
                        key={task.id}
                        className={`flex items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm transition-all duration-300 ${isCompleting ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-center gap-1.5 flex-1 min-w-0" onClick={() => handleTaskClick(task)}>
                          {task.notes && <AlignLeft className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />}
                          <span className={`text-sm transition-all duration-300 truncate ${isCompleting ? 'line-through text-gray-400' : ''}`}>{task.title}</span>
                        </div>
                        <div
                          className={`flex h-5 w-5 flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-2 transition-all duration-200 ${isCompleting ? 'border-zinc-950 bg-zinc-950' : 'border-zinc-300'}`}
                          onClick={() => handleToggleTask(task)}
                        >
                          {isCompleting && (
                            <svg className="w-3 h-3 text-white checkbox-tick" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="2,6 5,9 10,3" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {todoTasks.length === 0 && (
                    <div className="py-4 text-center text-sm text-zinc-400">No tasks to do</div>
                  )}
                </div>
              </div>

              {/* Completed - Fixed at bottom */}
              <div className="flex-shrink-0 border-t border-zinc-200 bg-white">
                <div
                  className="p-3 flex items-center justify-between cursor-pointer"
                  onClick={() => setMobileShowCompleted(!mobileShowCompleted)}
                >
                  <span className="text-xs font-semibold uppercase text-zinc-500">
                    Completed ({completedTasks.length})
                  </span>
                  <ChevronRight className={`h-4 w-4 text-zinc-500 transition-transform ${mobileShowCompleted ? 'rotate-90' : ''}`} />
                </div>
                {mobileShowCompleted && (
                  <div className="max-h-48 overflow-y-auto px-3 pb-3 space-y-2">
                    {completedTasks.map(task => (
                      <div
                        key={task.id}
                        className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 opacity-70"
                      >
                        <div
                          className="flex h-5 w-5 flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-zinc-950 bg-zinc-950"
                          onClick={() => handleToggleTask(task)}
                        >
                          <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="2,6 5,9 10,3" />
                          </svg>
                        </div>
                        <div className="flex items-center gap-1.5 flex-1 min-w-0" onClick={() => handleTaskClick(task)}>
                          {task.notes && <AlignLeft className="h-3.5 w-3.5 flex-shrink-0 text-zinc-300" />}
                          <span className="line-through text-gray-400 text-sm truncate">{task.title}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          <div className="flex-1 flex flex-col overflow-hidden" style={{ display: mobileTab === 'settings' ? 'flex' : 'none' }}>
            {/* Header - Fixed */}
            <div className="flex-shrink-0 border-b border-zinc-200 bg-white p-4 shadow-sm">
              <h1 className="flex items-center gap-2 text-lg font-semibold text-zinc-950">
                <Settings className="h-5 w-5 text-zinc-500" />
                Settings
              </h1>
            </div>

            {/* Settings Content - Scrollable */}
            <div className="flex-1 overflow-y-auto bg-zinc-100 p-4">
              <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
                <div
                  className="cursor-pointer border-b border-zinc-100 p-4 transition active:bg-zinc-50"
                  onClick={() => setShowSettingsModal(true)}
                >
                  <div className="text-sm font-medium text-zinc-950">Google Calendar</div>
                  <div className="text-xs text-zinc-500">
                    {googleConnected ? (googleUser?.email || 'Connected') : 'Not connected'}
                  </div>
                </div>
                <div
                  className="cursor-pointer border-b border-zinc-100 p-4 transition active:bg-zinc-50"
                  onClick={() => setShowSettingsModal(true)}
                >
                  <div className="text-sm font-medium text-zinc-950">Calendar Visibility</div>
                  <div className="text-xs text-zinc-500">Choose which calendars to show</div>
                </div>
                <div
                  className="cursor-pointer border-b border-zinc-100 p-4 transition active:bg-zinc-50"
                  onClick={() => setShowSettingsModal(true)}
                >
                  <div className="text-sm font-medium text-zinc-950">Secondary Timezone</div>
                  <div className="text-xs text-zinc-500">
                    {secondaryTimezone ? secondaryTimezoneAbbr + ' — ' + secondaryTimezone.split('/').pop().replace(/_/g, ' ') : 'None set'}
                  </div>
                </div>
                <div
                  className="flex items-center justify-between border-b border-zinc-100 p-4"
                  onClick={() => handleQuickCreateEnabledChange(!quickCreateEnabled)}
                >
                  <div>
                    <div className="text-sm font-medium text-zinc-950">Quick Event Creation</div>
                    <div className="text-xs text-zinc-500">Create events with just a title</div>
                  </div>
                  <Switch
                    checked={quickCreateEnabled}
                    color="dark/zinc"
                    onChange={handleQuickCreateEnabledChange}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div
                  className="cursor-pointer border-b border-zinc-100 p-4 transition active:bg-zinc-50"
                  onClick={() => window.location.reload()}
                >
                  <div className="text-sm font-medium text-zinc-950">Refresh Page</div>
                  <div className="text-xs text-zinc-500">Reload the app</div>
                </div>
                <div
                  className="cursor-pointer p-4 text-red-600 transition active:bg-red-50"
                  onClick={onLogout}
                >
                  <div className="text-sm font-medium">Sign Out</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Action Button - Tasks tab */}
        {mobileTab === 'todo' && !addTaskExpanded && (
          <MobileFloatingActionButton
            label="Add task"
            onClick={() => {
              if (!googleConnected) {
                setShowSettingsModal(true);
                return;
              }

              setAddTaskExpanded(true);
              // Focus synchronously first to satisfy iOS user-gesture requirement,
              // then re-focus after render to ensure input is mounted
              taskInputRef.current?.focus();
              requestAnimationFrame(() => taskInputRef.current?.focus());
            }}
          >
            <CheckSquare className="w-6 h-6 text-white" />
          </MobileFloatingActionButton>
        )}

        {/* Floating Action Button - Calendar tab only */}
        {mobileTab === 'calendar' && (
          <MobileFloatingActionButton
            label="Create event"
            onClick={() => handleCreateEvent(new Date())}
          >
            <Plus className="w-6 h-6 text-white" />
          </MobileFloatingActionButton>
        )}

        {/* Bottom Tab Bar - Fixed */}
        {renderBottomBar || (
          <div className="flex-shrink-0 border-t border-zinc-200 bg-white/95 px-2 pt-2 backdrop-blur" style={{ paddingBottom: 'max(10px, env(safe-area-inset-bottom))' }}>
            <div className="grid grid-cols-3 gap-1">
              {[
                { id: 'calendar', label: 'Calendar', icon: CalendarIcon },
                { id: 'todo', label: 'Tasks', icon: ListTodo },
                { id: 'settings', label: 'Settings', icon: Settings },
              ].map(({ id, label, icon: Icon }) => {
                const active = mobileTab === id;

                return (
                  <Button
                    key={id}
                    plain
                    onClick={() => setMobileTab(id)}
                    className={`flex min-h-[56px] flex-col gap-1 rounded-xl px-1 py-2 shadow-none ${
                      active ? '!bg-transparent text-blue-600 hover:!bg-transparent active:!bg-transparent' : 'text-zinc-500 hover:bg-zinc-50'
                    }`}
                  >
                    <Icon className={`h-5 w-5 ${active ? 'text-blue-600' : 'text-zinc-500'}`} />
                    <span className={`text-[11px] leading-none ${active ? 'font-bold text-blue-600' : 'font-medium'}`}>{label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* Modals */}
        {showEventView && (
          <EventViewModal
            event={selectedEvent}
            allEvents={events}
            onClose={() => { setShowEventView(false); setSelectedEvent(null); }}
            onEdit={handleEventViewEdit}
            onDelete={handleEventViewDelete}
          />
        )}
        {recurringDeleteEvent && (
          <Dialog size="md" onClose={() => setRecurringDeleteEvent(null)}>
            <DialogTitle>Delete recurring event</DialogTitle>
            <DialogDescription>
              Choose whether to delete this occurrence or the full series.
            </DialogDescription>
            <DialogBody className="space-y-2">
              <button type="button" onClick={() => handleRecurringDeleteChoice('single')} className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left transition hover:bg-zinc-50">
                <div className="font-medium text-zinc-950">This event only</div>
                <div className="text-xs text-zinc-500">Only delete this occurrence</div>
              </button>
              <button type="button" onClick={() => handleRecurringDeleteChoice('all')} className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left transition hover:bg-zinc-50">
                <div className="font-medium text-zinc-950">All events</div>
                <div className="text-xs text-zinc-500">Delete all events in the series</div>
              </button>
            </DialogBody>
            <DialogActions>
              <Button outline onClick={() => setRecurringDeleteEvent(null)}>Cancel</Button>
            </DialogActions>
          </Dialog>
        )}
        {showEventModal && (
          <EventModal
            event={selectedEvent}
            onClose={() => { setShowEventModal(false); setSelectedEvent(null); }}
            onSave={() => { invalidateEvents(); setShowEventModal(false); setSelectedEvent(null); }}
          />
        )}
        {showTaskModal && (
          <TaskModal
            task={selectedTask}
            onClose={handleTaskModalClose}
            onSave={handleTaskSave}
          />
        )}
        {showSettingsModal && (
          <SettingsModal
            onClose={() => setShowSettingsModal(false)}
            hiddenCalendars={hiddenCalendars}
            onToggleCalendar={handleToggleCalendarVisibility}
            dimKeywords={dimKeywords}
            onDimKeywordsChange={handleDimKeywordsChange}
            secondaryTimezone={secondaryTimezone}
            onSecondaryTimezoneChange={handleSecondaryTimezoneChange}
            quickCreateEnabled={quickCreateEnabled}
            onQuickCreateEnabledChange={handleQuickCreateEnabledChange}
            googleConnected={googleConnected}
            googleUser={googleUser}
            onConnectGoogle={onConnectGoogle}
            onDisconnectGoogle={onDisconnectGoogle}
          />
        )}
        <EventContextMenu />
      </div>
    );
  }

  // Desktop View
  return (
    <div className="flex h-full w-full overflow-hidden bg-zinc-100 font-sans text-zinc-950">
      <div
        className="flex shrink-0 select-none flex-col bg-zinc-100"
        style={{ width: `${sidebarWidth}px` }}
      >
        {/* Mini Calendar */}
        <div className="mb-4 min-h-[300px] p-4 pt-6">
           <div className="mb-5 flex items-center justify-between px-2">
            <span className="text-lg font-semibold text-zinc-950">{miniCalDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
            <div className="flex gap-1">
              <Button plain className="h-8 w-8 rounded-full p-0" onClick={() => setMiniCalDate(new Date(miniCalDate.getFullYear(), miniCalDate.getMonth() - 1))} aria-label="Previous month">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button plain className="h-8 w-8 rounded-full p-0" onClick={() => setMiniCalDate(new Date(miniCalDate.getFullYear(), miniCalDate.getMonth() + 1))} aria-label="Next month">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="mb-3 grid grid-cols-7 text-center text-xs font-bold text-zinc-500">
            {DAYS_SHORT.map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 text-center text-sm">
            {miniCalMonthDays.slice(0, 35).map((d, i) => (
              <div
                key={i}
                onClick={() => handleMiniCalendarDateSelect(d.date, { syncMiniMonth: !d.currentMonth })}
                className={`cursor-pointer rounded-full py-[7px] transition-colors ${isSameDay(d.date, currentDate) ? 'bg-zinc-950 font-bold text-white' : 'text-zinc-700 hover:bg-white'}`}
              >
                {d.date.getDate()}
              </div>
            ))}
          </div>
        </div>

        {/* My Tasks */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-zinc-200">
          <div className="px-4 py-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-zinc-950"><CheckSquare className="h-5 w-5 text-zinc-500" /> My Tasks</h3>
          </div>
          <div className="px-4 py-4">
             <div className="relative">
                <Input
                  type="text"
                  placeholder="Add a task"
                  value={newTaskText}
                  onChange={(e) => setNewTaskText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTask();
                    }
                  }}
                  className="pl-9"
                />
                <Plus className="absolute left-3 top-3 h-4 w-4 cursor-pointer text-zinc-500" onClick={handleAddTask} />
             </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col bg-zinc-100">
            {/* To Do Section - Scrollable */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <div>
                <div
                  className="flex cursor-pointer items-center justify-between px-4 py-2 transition-colors hover:bg-white"
                  onClick={() => setShowTodoTasks(!showTodoTasks)}
                >
                  <span className="text-xs font-semibold uppercase text-zinc-500">To do ({todoTasks.length})</span>
                  <ChevronRight className={`h-4 w-4 text-zinc-500 transition-transform ${showTodoTasks ? 'rotate-90' : ''}`} />
                </div>
                {showTodoTasks && (
                  <div className="space-y-2 bg-zinc-100 px-4 py-3">
                    {todoTasks.map(task => {
                      const isCompleting = completingTasks.has(task.id);
                      return (
                        <div
                          key={task.id}
                          className={`flex items-start gap-3 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm transition-all hover:border-zinc-300 hover:shadow-md ${task._optimistic ? 'cursor-default opacity-60' : 'cursor-move'} ${isCompleting ? 'task-completing' : ''}`}
                          draggable={!task._optimistic}
                          onDragStart={(e) => handleTaskDragStart(task, e)}
                          onDragEnd={handleTaskDragEnd}
                        >
                          <div
                            className={`mt-0.5 flex h-4 min-h-[16px] w-4 min-w-[16px] flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-2 ${isCompleting ? 'border-zinc-950 bg-zinc-950 checkbox-tick' : 'border-zinc-300'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleTask(task);
                            }}
                          >
                            {isCompleting && (
                              <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="2,6 5,9 10,3" />
                              </svg>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 flex-1 min-w-0 cursor-pointer" onClick={() => handleTaskClick(task)}>
                            {task.notes && <AlignLeft className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />}
                            <span className={`truncate text-[14px] font-medium ${isCompleting ? 'text-zinc-400' : 'text-zinc-800'}`}>
                              {task.title}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                    {todoTasks.length === 0 && (
                      <div className="py-4 text-center text-xs text-zinc-500">No tasks to do</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Completed Section - Sticky at Bottom */}
            <div className="bg-zinc-100">
              {showCompletedTasks && (
                <div className="px-4 pt-3 space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar">
                  {completedTasks.map(task => (
                    <div key={task.id} className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-white p-3 opacity-80 shadow-sm transition-all hover:border-zinc-300">
                      <div
                        className="mt-0.5 flex h-4 min-h-[16px] w-4 min-w-[16px] flex-shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-zinc-950 bg-zinc-950"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleTask(task);
                        }}
                      >
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="white">
                          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" fill="none"/>
                        </svg>
                      </div>
                      <div className="flex items-center gap-1.5 flex-1 min-w-0 cursor-pointer" onClick={() => handleTaskClick(task)}>
                        {task.notes && <AlignLeft className="h-3.5 w-3.5 flex-shrink-0 text-zinc-300" />}
                        <span className="text-[14px] text-gray-400 truncate">
                          {task.title}
                        </span>
                      </div>
                    </div>
                  ))}
                  {completedTasks.length === 0 && (
                    <div className="py-4 text-center text-xs text-zinc-500">No completed tasks</div>
                  )}
                </div>
              )}
              <div
                className="flex cursor-pointer items-center justify-between px-4 py-2 transition-colors hover:bg-white"
                onClick={() => setShowCompletedTasks(!showCompletedTasks)}
              >
                <span className="text-xs font-semibold uppercase text-zinc-500">Completed ({completedTasks.length})</span>
                <ChevronRight className={`h-4 w-4 text-zinc-500 transition-transform ${showCompletedTasks ? 'rotate-90' : ''}`} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        role="separator"
        aria-label="Resize sidebar"
        aria-orientation="vertical"
        aria-valuemin={MIN_SIDEBAR_WIDTH}
        aria-valuemax={clampSidebarWidth(MAX_SIDEBAR_WIDTH)}
        aria-valuenow={sidebarWidth}
        tabIndex={0}
        title="Drag to resize sidebar"
        onPointerDown={handleSidebarResizeStart}
        onKeyDown={handleSidebarResizeKeyDown}
        onDoubleClick={resetSidebarWidth}
        className={clsx(
          'group relative z-20 flex w-3 shrink-0 cursor-col-resize items-center justify-center bg-zinc-100 outline-none transition',
          'hover:bg-zinc-200/70 focus-visible:bg-zinc-200/70',
          isResizingSidebar && 'bg-zinc-200'
        )}
      >
        <div
          className={clsx(
            'h-12 w-1 rounded-full transition',
            isResizingSidebar ? 'bg-zinc-950' : 'bg-zinc-300 group-hover:bg-zinc-400 group-focus-visible:bg-zinc-400'
          )}
        />
      </div>

      {/* Main Content Area */}
      <div className="min-w-0 flex-1 p-3">
        <section className="flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
          <div className="relative flex h-16 shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-6">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => handleCreateEvent(currentDate)}
              >
                <Plus className="h-4 w-4" /> Create
              </Button>
              <Button onClick={goToToday} outline>
                {(() => {
                  const today = new Date();
                  const day = today.getDate();
                  const suffix = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th';
                  const month = today.toLocaleDateString('en-US', { month: 'short' });
                  return `Today - ${day}${suffix} ${month}`;
                })()}
              </Button>
              <div className="ml-2 flex items-center gap-1 text-zinc-500">
                <Button plain className="h-9 w-9 rounded-full p-0" onClick={() => handleNavigate(-1)} aria-label="Previous range">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button plain className="h-9 w-9 rounded-full p-0" onClick={() => handleNavigate(1)} aria-label="Next range">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <h2 className="absolute left-1/2 -translate-x-1/2 transform text-xl font-semibold text-zinc-950">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <div className="flex items-center gap-3">
              <div className="flex overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50 p-1 shadow-sm">
                {Object.values(VIEW_TYPES).map(v => (
                  <Button
                    key={v}
                    plain
                    onClick={() => setView(v)}
                    className={`rounded-md px-3 py-1 text-xs font-semibold shadow-none ${view === v ? 'bg-white text-zinc-950 shadow-sm hover:bg-white' : 'text-zinc-500 hover:bg-zinc-100'}`}
                  >
                    {v}
                  </Button>
                ))}
              </div>
              <Button
                plain
                onClick={() => setShowSettingsModal(true)}
                className="h-9 w-9 rounded-full p-0"
                title="Settings"
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button
                plain
                onClick={() => window.location.reload()}
                className="h-9 w-9 rounded-full p-0"
                title="Refresh"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {!canRenderCalendar ? (
              <GoogleConnectionPrompt />
            ) : googleConnected && loading ? (
              <div className="flex-1 flex flex-col">
                {view === VIEW_TYPES.MONTH ? (
                  <div className="grid h-full flex-1 grid-cols-7 grid-rows-6 border-t border-zinc-200">
                    {[...Array(42)].map((_, i) => (
                      <div key={i} className="flex flex-col border-b border-r border-zinc-200 p-1">
                        <div className="flex flex-col items-center pt-1">
                          {i < 7 && <div className="mb-1 h-2.5 w-6 animate-pulse rounded bg-zinc-200" />}
                          <div className="h-7 w-7 animate-pulse rounded-full bg-zinc-100 ring-1 ring-inset ring-zinc-200" />
                        </div>
                        {i % 3 === 0 && <div className="mt-1 h-4 w-full animate-pulse rounded-md bg-blue-50 ring-1 ring-inset ring-blue-100" />}
                        {i % 5 === 0 && <div className="mt-1 h-4 w-3/4 animate-pulse rounded-md bg-zinc-100 ring-1 ring-inset ring-zinc-200" />}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex-1 flex">
                    <div className="w-16 border-r border-zinc-200">
                      {[...Array(12)].map((_, i) => (
                        <div key={i} className="h-16 flex items-start justify-end pr-2 pt-1">
                          <div className="h-3 w-8 animate-pulse rounded bg-zinc-200" />
                        </div>
                      ))}
                    </div>
                    <div className="flex-1 p-4 space-y-3">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-12 animate-pulse rounded-lg border border-zinc-200 bg-white shadow-sm" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {view === VIEW_TYPES.MONTH && (
                  <div className="grid h-full flex-1 grid-cols-7 grid-rows-6 border-t border-zinc-200">
                    {monthDays.map((d, i) => {
                      const dayEvents = getEventsForDay(d.date);
                      const isToday = isSameDay(d.date, new Date());
                      return (
                        <div
                          key={i}
                          data-main-calendar-glow-date={getDateKey(d.date)}
                          data-main-calendar-glow-class="main-calendar-selected-day-cell-glow"
                          className={clsx(
                            'flex flex-col border-b border-r border-zinc-200 p-1 transition-colors hover:bg-zinc-50',
                            !d.currentMonth ? 'bg-zinc-100' : isWeekend(d.date) ? 'bg-zinc-50' : 'bg-white'
                          )}
                          onClick={() => handleCreateEvent(d.date)}
                        >
                          <div className="flex flex-col items-center pt-1">
                            {i < 7 && <span className="mb-1 text-[10px] font-bold uppercase tracking-normal text-zinc-500">{DAYS_SHORT[i]}</span>}
                            <span
                              data-main-calendar-glow-date={getDateKey(d.date)}
                              data-main-calendar-glow-class="main-calendar-selected-day-glow"
                              className={clsx(
                                'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold',
                                isToday ? 'bg-zinc-950 text-white shadow-sm' : d.currentMonth ? 'text-zinc-800' : 'text-zinc-400'
                              )}
                            >
                              {d.date.getDate()}
                            </span>
                          </div>
                          <div className="flex-1 overflow-hidden mt-1">
                            {dayEvents.slice(0, 3).map((event) => {
                              const eventEnd = event.end?.dateTime ? new Date(event.end.dateTime) : null;
                              const isPastEvent = isSameDay(d.date, new Date()) && eventEnd && eventEnd < now;
                              const linkedTask = getLinkedTaskForEvent(event);
                              return (
                                <CalendarEventBadge
                                  key={event.id}
                                  event={event}
                                  past={isPastEvent}
                                  compact
                                  linkedTask={linkedTask}
                                  linkedTaskCompleting={linkedTask ? completingTasks.has(linkedTask.id) : false}
                                  onToggleLinkedTask={(e) => handleToggleLinkedTask(linkedTask, e)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEventClick(event);
                                  }}
                                  onContextMenu={(e) => handleEventContextMenu(event, e)}
                                />
                              );
                            })}
                            {dayEvents.length > 3 && (
                              <Badge color="zinc" className="px-1.5 py-0.5 text-[9px]">+{dayEvents.length - 3} more</Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {view === VIEW_TYPES.WEEK && <HourlyGrid activeDays={weekDays} initialScroll="end" />}
                {view === VIEW_TYPES.SEVEN_DAYS && <HourlyGrid activeDays={sevenDayRange} initialScroll="end" />}
                {view === VIEW_TYPES.DAY && <HourlyGrid activeDays={[currentDate]} />}
              </>
            )}
          </div>
        </section>
      </div>

      {showEventView && (
        <EventViewModal
          event={selectedEvent}
          allEvents={events}
          onClose={() => { setShowEventView(false); setSelectedEvent(null); }}
          onEdit={handleEventViewEdit}
          onDelete={handleEventViewDelete}
        />
      )}

      {recurringDeleteEvent && (
        <Dialog size="md" onClose={() => setRecurringDeleteEvent(null)}>
          <DialogTitle>Delete recurring event</DialogTitle>
          <DialogDescription>
            Choose whether to delete this occurrence or the full series.
          </DialogDescription>
          <DialogBody className="space-y-2">
            <button type="button" onClick={() => handleRecurringDeleteChoice('single')} className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left transition hover:bg-zinc-50">
              <div className="font-medium text-zinc-950">This event only</div>
              <div className="text-xs text-zinc-500">Only delete this occurrence</div>
            </button>
            <button type="button" onClick={() => handleRecurringDeleteChoice('all')} className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left transition hover:bg-zinc-50">
              <div className="font-medium text-zinc-950">All events</div>
              <div className="text-xs text-zinc-500">Delete all events in the series</div>
            </button>
          </DialogBody>
          <DialogActions>
            <Button outline onClick={() => setRecurringDeleteEvent(null)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}

      {showEventModal && (
        <EventModal
          event={selectedEvent}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onSave={() => {
            invalidateEvents();
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
        />
      )}

      {showTaskModal && (
        <TaskModal
          task={selectedTask}
          onClose={handleTaskModalClose}
          onSave={handleTaskSave}
        />
      )}

      {showSettingsModal && (
        <SettingsModal
          onClose={() => setShowSettingsModal(false)}
          hiddenCalendars={hiddenCalendars}
          onToggleCalendar={handleToggleCalendarVisibility}
          dimKeywords={dimKeywords}
          onDimKeywordsChange={handleDimKeywordsChange}
          secondaryTimezone={secondaryTimezone}
          onSecondaryTimezoneChange={handleSecondaryTimezoneChange}
          quickCreateEnabled={quickCreateEnabled}
          onQuickCreateEnabledChange={handleQuickCreateEnabledChange}
          googleConnected={googleConnected}
          googleUser={googleUser}
          onConnectGoogle={onConnectGoogle}
          onDisconnectGoogle={onDisconnectGoogle}
        />
      )}

      {quickCreateData && <QuickCreatePopover />}
      <EventContextMenu />

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #dadce0; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #bdc1c6; }

        @keyframes checkboxTick {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }

        @keyframes fadeOut {
          0% { opacity: 1; transform: translateX(0); }
          100% { opacity: 0; transform: translateX(10px); }
        }

        @keyframes mainCalendarSelectedDayGlow {
          0% {
            box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
            transform: scale(1);
          }
          18% {
            box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.16), 0 0 20px rgba(37, 99, 235, 0.22);
            transform: scale(1.03);
          }
          100% {
            box-shadow: 0 0 0 12px rgba(37, 99, 235, 0), 0 0 0 rgba(37, 99, 235, 0);
            transform: scale(1);
          }
        }

        @keyframes mainCalendarSelectedSurfaceGlow {
          0% {
            background-color: rgba(239, 246, 255, 0);
            box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0);
          }
          18% {
            background-color: rgba(239, 246, 255, 0.92);
            box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.2), 0 0 24px rgba(37, 99, 235, 0.1);
          }
          100% {
            background-color: rgba(239, 246, 255, 0);
            box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0);
          }
        }

        .checkbox-tick svg {
          animation: checkboxTick 0.2s ease-out;
        }

        .task-completing {
          animation: fadeOut 0.3s ease-out 0.3s forwards;
        }

        .task-drop-slot-hover {
          background-color: rgb(239 246 255);
          box-shadow: inset 0 0 0 2px rgb(96 165 250);
        }

        .main-calendar-selected-day-glow {
          animation: mainCalendarSelectedDayGlow 1.3s ease-out;
        }

        .main-calendar-selected-day-surface-glow,
        .main-calendar-selected-day-cell-glow {
          animation: mainCalendarSelectedSurfaceGlow 1.3s ease-out;
        }

        @media (prefers-reduced-motion: reduce) {
          .main-calendar-selected-day-glow,
          .main-calendar-selected-day-surface-glow,
          .main-calendar-selected-day-cell-glow {
            animation: none;
            box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.18);
          }
        }

      `}} />
    </div>
  );
}
